import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Row, Col, Table, Button, Badge, Modal, Form, Spinner, Alert, InputGroup } from 'react-bootstrap';
import {
    Plus, Edit, Trash2, RefreshCw, CheckCircle, XCircle, AlertTriangle, Clock,
    Calculator, ArrowLeftRight, Save, ChevronDown, ChevronUp, Zap
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
    getSentinelRoutes,
    createSentinelRoute,
    updateSentinelRoute,
    deleteSentinelRoute,
    getSentinelLogs,
    applySentinelTariff,
    triggerSentinelCheck
} from '../api';
import { useLanguage } from '../context/LanguageContext';
import AddressAutocomplete from './AddressAutocomplete';

const COUNTRY_FLAGS = {
    'Chile': '🇨🇱 Chile',
    'Brasil': '🇧🇷 Brasil',
    'Peru': '🇵🇪 Perú',
    'Argentina': '🇦🇷 Argentina',
    'Dominican Republic': '🇩🇴 Rep. Dominicana'
};

const COUNTRY_TO_ISO_CODE = {
    'Chile': 'cl',
    'Brasil': 'br',
    'Peru': 'pe',
    'Argentina': 'ar',
    'Dominican Republic': 'do',
    'Colombia': 'co'
};

const AIRPORT_BOOKING_SUGGESTIONS = {
    'PUJ': 'Punta Cana International Airport (PUJ), Punta Cana, Dominican Republic',
    'SDQ': 'Las Americas International Airport (SDQ), Santo Domingo, Dominican Republic',
    'SCL': 'Arturo Merino Benitez Airport (SCL), Santiago, Chile',
    'IQQ': 'Diego Aracena International Airport (IQQ), Iquique, Chile',
    'CJC': 'El Loa Airport (CJC), Calama, Chile',
    'LSC': 'La Florida Airport (LSC), La Serena, Chile',
    'ANF': 'Andres Sabella Galvez International Airport (ANF), Antofagasta, Chile',
    'GRU': 'Guarulhos International Airport (GRU), Sao Paulo, Brazil',
    'CGH': 'Congonhas Airport (CGH), Sao Paulo, Brazil',
    'VCP': 'Viracopos International Airport (VCP), Campinas, Brazil',
    'BCP': 'Viracopos International Airport (VCP), Campinas, Brazil',
    'GIG': 'Rio de Janeiro/Galeao International Airport (GIG), Rio de Janeiro, Brazil',
    'SDU': 'Santos Dumont Airport (SDU), Rio de Janeiro, Brazil',
    'EZE': 'Ministro Pistarini International Airport (EZE), Buenos Aires, Argentina',
    'AEP': 'Jorge Newbery Airfield (AEP), Buenos Aires, Argentina',
    'MDZ': 'Governor Francisco Gabrielli International Airport (MDZ), Mendoza, Argentina',
    'LIM': 'Jorge Chavez International Airport (LIM), Lima, Peru'
};

const getSuggestedAirportForZone = (zoneName = '') => {
    if (!zoneName) return null;
    const upper = String(zoneName).toUpperCase().trim();
    if (AIRPORT_BOOKING_SUGGESTIONS[upper]) return AIRPORT_BOOKING_SUGGESTIONS[upper];
    for (const [iata, fullText] of Object.entries(AIRPORT_BOOKING_SUGGESTIONS)) {
        const regex = new RegExp(`(^|[^A-Z0-9])${iata}([^A-Z0-9]|$)`);
        if (regex.test(upper)) return fullText;
    }
    return null;
};

const CURRENCY_SYMBOLS = {
    'CLP': '$',
    'BRL': 'R$',
    'USD': 'US$',
    'PEN': 'S/',
    'ARS': 'ARS $',
    'DOP': 'RD$',
    'EUR': '€'
};

const MAX_ROUTES_PER_PROVIDER = 8;

const SENTINEL_I18N = {
    es: {
        headerTitle: '🤖 Bot Centinela & Simulador de Precios en Booking.com',
        routeQuota: 'Cupo de Rutas',
        maxLabel: 'máx.',
        providerDesc: 'Revisa en tus zonas si estás Ganando (#1) o Perdiendo en Booking.com. Usa la Calculadora al lado de cada vehículo para simular un nuevo precio o activar ⭐ Genius (-5% para ti, pero Booking baja un 10% al cliente final) y actualiza tu tarifa Ida y Vuelta con un clic.',
        adminDesc: 'El Centinela audita las rutas de cada proveedor desde 5 servidores aislados cada 12 horas (o bajo demanda con el botón de Auditar), cruzando las 7 categorías de Booking.com con nuestras tarifas y permitiendo simular o ajustar precios Ida y Vuelta en vivo.',
        refreshBtn: 'Actualizar Vista',
        auditNowBtn: '⚡ Auditar en Booking Ahora',
        auditRouteBtn: '⚡ Auditar Ruta',
        auditingBtn: 'Encolando...',
        addRouteProviderBtn: 'Agregar Ruta',
        addRouteAdminBtn: 'Nueva Ruta a Vigilar',
        kpiRoutesProvider: 'Mis Rutas en Mis Zonas',
        kpiRoutesAdmin: 'Rutas Vigiladas Activas',
        kpiSafeCheck: 'Chequeo automático cada 12h + Botón Manual',
        kpiWinRate: 'Win Rate (Posición #1)',
        kpiValidChecks: 'chequeos válidos',
        kpiWinsTitle: 'Ganando #1 en Booking',
        kpiWinsSub: 'Veces siendo la opción más barata',
        kpiLossesTitle: 'Ganadas por Competencia',
        kpiLossesSub: 'Ajustables con la calculadora',
        countryLabel: 'País:',
        allCountries: '🌐 Todas',
        providerLabel: 'Proveedor:',
        allProviders: 'Todos los Proveedores',
        emptyTitle: 'No hay rutas configuradas para mostrar en esta vista',
        emptyProviderDesc: 'Agrega hasta 8 rutas clave de tus zonas (ej. Aeropuerto ➔ Hotel principal) para que el Bot Centinela revise a cómo vende la competencia y te ayude a ganar el puesto #1.',
        emptyAdminDesc: 'No hay rutas que coincidan con el filtro seleccionado.',
        addFirstRouteBtn: 'Agregar Mi Primera Ruta a Vigilar',
        tariffStretchBadge: '📍 Tramo Tarifario:',
        catsWonSuffix: 'categorías ganadas',
        searchedOnBooking: 'Buscado en Booking.com:',
        daysSuffix: 'días',
        lastCheckLabel: 'Último chequeo:',
        inQueueLabel: '⚡ En cola prioritaria (Próximo turno)',
        editRouteBtn: 'Editar Ruta',
        pendingFirstCheckMsg: '⏳ Esta ruta está en cola prioritaria para su inspección en Booking.com. En el próximo turno del Centinela aparecerán aquí los precios de todas las categorías.',
        thCategory: 'Categoría / Vehículo',
        thStatus: 'Estado Actual',
        thCompetitor: 'Competidor #1 en Booking',
        thYourTariff: 'Tu Tarifa Actual',
        thRecommendation: '🎯 Recomendación para Ganar #1',
        thCalculator: 'Calculadora / Simulador en Vivo + Genius',
        thSave: 'Guardar Tarifa',
        yourVehicle: '🚗 Tu vehículo:',
        pendingBadge: '⏳ Pendiente chequeo',
        winningBadge: 'GANANDO #1',
        losingBadge: 'PERDIENDO',
        noOffersBadge: 'Sin Ofertas',
        publicWebPrice: 'Público Web:',
        includesCommission: '(Incluye comisión web Booking)',
        costOneWay: 'Costo Ida:',
        costReturn: 'Costo Vuelta:',
        geniusActiveBadge: '⭐ Genius Activo',
        youLabel: 'tú',
        geniusInactiveBadge: 'Genius Inactivo (0%)',
        bidBadge: 'Puja',
        noTariffLoaded: 'Sin tarifa cargada en este vehículo',
        withoutGeniusLabel: 'Sin Genius (Normal):',
        chargeMax: 'Cobrar máx.',
        testBtn: 'Probar',
        withGeniusLabel: '⭐ Con Genius (-5% tú ➔ -10% Web):',
        tariffUpTo: 'Tarifa hasta',
        netReceive: 'Recibes neto:',
        withoutLoweringPrice: '(¡Sin bajar tu precio!)',
        useGeniusBtn: 'Usar Genius',
        waitingNextCheck: 'Esperando próximo chequeo web para calcular meta',
        simulateBaseTariff: 'Simular Tu Tarifa Base',
        geniusProgram: '⭐ Programa Genius',
        geniusSwitchActive: 'Activo (-10% Booking)',
        geniusSwitchInactive: 'Inactivo (0%)',
        simWinMsg: '🟢 ¡CON ESTE PRECIO GANAS #1!',
        simLoseMsg: '🔴 SEGUIRÍAS PERDIENDO',
        simReadyMsg: '💡 Simulación de Tarifa Lista',
        geniusEffectTitle: 'Efecto Genius:',
        geniusEffectDesc1: 'Aportas 5%',
        geniusEffectDesc2: 'y Booking baja un 10% el precio final al pasajero.',
        lowerMorePrefix: 'Baja',
        lowerMoreMid: 'más',
        orActivateGenius: 'o activa ⭐ Genius ',
        toWinRank1: 'para pasar al puesto #1.',
        notEditable: 'No editable',
        applyBothDirs: 'Aplicar Ida y Vuelta',
        savingBtn: 'Guardando...',
        saveBothBtn: 'Guardar Ida y Vuelta',
        saveOneWayBtn: 'Guardar 1 Sentido',
        historyTitle: '📋 Historial Reciente de Inspecciones en Booking.com',
        historySub: 'Registro de las últimas consultas ejecutadas por el Bot Centinela en tus rutas.',
        loadingSentinel: 'Cargando datos del Centinela...',
        emptyHistory: 'Aún no se registran ejecuciones para estas rutas. El Bot Centinela poblará esta tabla automáticamente en su próximo turno.',
        thDateTime: 'Fecha / Hora',
        thRoute: 'Ruta',
        thSearchedDate: 'Fecha Buscada',
        thResultStd: 'Resultado (Std)',
        thProvider1: 'Proveedor #1 en Booking',
        thPublicWinner: 'Precio Público Ganador',
        thDetail: 'Detalle',
        viewCategoriesBtn: 'Ver Categorías',
        modalEditTitle: '✏️ Editar Ruta:',
        modalCreateTitle: '➕ Agregar Ruta al Bot Centinela (Máx. 8 por proveedor)',
        step1SelectZones: '1. Selecciona el Tramo de tu Tarifario (Zonas donde tienes precios)',
        selectZonePlaceholder: '-- Selecciona Zona Origen ➔ Zona Destino --',
        step1Help: 'Esto vincula la ruta directamente con tus tarifas (Ida y Vuelta) para que puedas simular y actualizar tus precios desde el Centinela.',
        routeShortName: 'Nombre Corto de la Ruta',
        countryField: 'País',
        originField: 'Dirección o Aeropuerto de Origen (tal como se busca en Booking.com)',
        destField: 'Dirección o Hotel de Destino (dentro de la zona seleccionada)',
        paxField: 'Pasajeros a Cotizar',
        daysAheadField: 'Días de Anticipación',
        pickupTimeField: 'Hora de Recogida',
        activeSwitchLabel: 'Ruta activa para vigilancia automática',
        cancelBtn: 'Cancelar',
        saveRouteBtn: '💾 Guardar Ruta',
        modalDetailTitle: '📊 Detalle de Inspección',
        maxRoutesReachedToast: 'Has alcanzado el máximo de 8 rutas en tu Bot Centinela. Edita o elimina una ruta para agregar otra.',
        fillRequiredToast: 'Por favor completa el Nombre de la Ruta, Origen y Destino.',
        selectZoneToast: 'Por favor selecciona el tramo de tus zonas tarifadas.',
        routeUpdatedToast: 'Ruta actualizada en el Bot Centinela.',
        routeCreatedToast: 'Nueva ruta agregada al Bot Centinela.',
        confirmDeleteRoute: '¿Seguro que deseas eliminar esta ruta del Centinela?',
        routeDeletedToast: 'Ruta eliminada.',
        invalidCostToast: 'Ingresa un costo válido mayor a 0.',
        bothDirectionsToast: 'Ida y Vuelta (ambos sentidos)',
        oneWayToast: '1 sentido',
        tariffUpdatedToast: 'Tarifa actualizada en'
    },
    pt: {
        headerTitle: '🤖 Robô Sentinela & Simulador de Preços na Booking.com',
        routeQuota: 'Limite de Rotas',
        maxLabel: 'máx.',
        providerDesc: 'Veja nas suas zonas se você está Ganhando (#1) ou Perdendo na Booking.com. Use a Calculadora ao lado de cada veículo para simular um novo preço ou ativar o ⭐ Genius (-5% para você, mas a Booking reduz 10% para o passageiro final) e atualize sua tarifa de Ida e Volta com um clique.',
        adminDesc: 'O Sentinela audita as rotas de cada parceiro a partir de 5 servidores isolados a cada 12 horas (ou sob demanda), cruzando as 7 categorias da Booking.com com nossas tarifas e permitindo simular ou ajustar preços de Ida e Volta ao vivo.',
        refreshBtn: 'Atualizar Vista',
        auditNowBtn: '⚡ Auditar na Booking Agora',
        auditRouteBtn: '⚡ Auditar Rota',
        auditingBtn: 'Enfileirando...',
        addRouteProviderBtn: 'Adicionar Rota',
        addRouteAdminBtn: 'Nova Rota para Monitorar',
        kpiRoutesProvider: 'Minhas Rotas nas Minhas Zonas',
        kpiRoutesAdmin: 'Rotas Monitoradas Ativas',
        kpiSafeCheck: 'Verificação a cada 12h + Botão Manual',
        kpiWinRate: 'Taxa de Vitória (Posição #1)',
        kpiValidChecks: 'verificações válidas',
        kpiWinsTitle: 'Ganhando #1 na Booking',
        kpiWinsSub: 'Vezes sendo a opção mais barata',
        kpiLossesTitle: 'Vencidas pela Concorrência',
        kpiLossesSub: 'Ajustáveis com a calculadora',
        countryLabel: 'País:',
        allCountries: '🌐 Todas',
        providerLabel: 'Parceiro:',
        allProviders: 'Todos os Parceiros',
        emptyTitle: 'Nenhuma rota configurada para exibir nesta visualização',
        emptyProviderDesc: 'Adicione até 8 rotas principais das suas zonas (ex: Aeroporto ➔ Hotel principal) para que o Robô Sentinela verifique os preços da concorrência e ajude você a ganhar a posição #1.',
        emptyAdminDesc: 'Nenhuma rota corresponde ao filtro selecionado.',
        addFirstRouteBtn: 'Adicionar Minha Primeira Rota',
        tariffStretchBadge: '📍 Trecho Tarifário:',
        catsWonSuffix: 'categorias ganhas',
        searchedOnBooking: 'Buscado na Booking.com:',
        daysSuffix: 'dias',
        lastCheckLabel: 'Última verificação:',
        inQueueLabel: '⚡ Na fila prioritária (Próximo turno)',
        editRouteBtn: 'Editar Rota',
        pendingFirstCheckMsg: '⏳ Esta rota está na fila prioritária para inspeção na Booking.com. No próximo turno do Sentinela, os preços de todas as categorias aparecerão aqui.',
        thCategory: 'Categoria / Veículo',
        thStatus: 'Status Atual',
        thCompetitor: 'Concorrente #1 na Booking',
        thYourTariff: 'Sua Tarifa Atual',
        thRecommendation: '🎯 Recomendação para Ganhar #1',
        thCalculator: 'Calculadora / Simulador ao Vivo + Genius',
        thSave: 'Salvar Tarifa',
        yourVehicle: '🚗 Seu veículo:',
        pendingBadge: '⏳ Aguardando verificação',
        winningBadge: 'GANHANDO #1',
        losingBadge: 'PERDENDO',
        noOffersBadge: 'Sem Ofertas',
        publicWebPrice: 'Preço Público Web:',
        includesCommission: '(Inclui comissão web da Booking)',
        costOneWay: 'Custo Ida:',
        costReturn: 'Custo Volta:',
        geniusActiveBadge: '⭐ Genius Ativo',
        youLabel: 'você',
        geniusInactiveBadge: 'Genius Inativo (0%)',
        bidBadge: 'Lance',
        noTariffLoaded: 'Sem tarifa cadastrada neste veículo',
        withoutGeniusLabel: 'Sem Genius (Normal):',
        chargeMax: 'Cobrar máx.',
        testBtn: 'Testar',
        withGeniusLabel: '⭐ Com Genius (-5% você ➔ -10% Web):',
        tariffUpTo: 'Tarifa até',
        netReceive: 'Você recebe líquido:',
        withoutLoweringPrice: '(Sem baixar seu preço!)',
        useGeniusBtn: 'Usar Genius',
        waitingNextCheck: 'Aguardando próxima verificação web para calcular meta',
        simulateBaseTariff: 'Simular Sua Tarifa Base',
        geniusProgram: '⭐ Programa Genius',
        geniusSwitchActive: 'Ativo (-10% Booking)',
        geniusSwitchInactive: 'Inactivo (0%)',
        simWinMsg: '🟢 COM ESTE PREÇO VOCÊ GANHA #1!',
        simLoseMsg: '🔴 CONTINUARIA PERDENDO',
        simReadyMsg: '💡 Simulação de Tarifa Pronta',
        geniusEffectTitle: 'Efeito Genius:',
        geniusEffectDesc1: 'Você contribui com 5%',
        geniusEffectDesc2: 'e a Booking reduz 10% no preço final para o passageiro.',
        lowerMorePrefix: 'Reduza mais',
        lowerMoreMid: '',
        orActivateGenius: 'ou ative o ⭐ Genius ',
        toWinRank1: 'para assumir a posição #1.',
        notEditable: 'Não editável',
        applyBothDirs: 'Aplicar Ida e Volta',
        savingBtn: 'Salvando...',
        saveBothBtn: 'Salvar Ida e Volta',
        saveOneWayBtn: 'Salvar 1 Sentido',
        historyTitle: '📋 Histórico Recente de Inspeções na Booking.com',
        historySub: 'Registro das últimas consultas executadas pelo Robô Sentinela nas suas rotas.',
        loadingSentinel: 'Carregando dados do Sentinela...',
        emptyHistory: 'Ainda não há execuções registradas para estas rotas. O Robô Sentinela preencherá esta tabela automaticamente no próximo turno.',
        thDateTime: 'Data / Hora',
        thRoute: 'Rota',
        thSearchedDate: 'Data Buscada',
        thResultStd: 'Resultado (Std)',
        thProvider1: 'Parceiro #1 na Booking',
        thPublicWinner: 'Preço Público Vencedor',
        thDetail: 'Detalhe',
        viewCategoriesBtn: 'Ver Categorias',
        modalEditTitle: '✏️ Editar Rota:',
        modalCreateTitle: '➕ Adicionar Rota ao Robô Sentinela (Máx. 8 por parceiro)',
        step1SelectZones: '1. Selecione o Trecho do seu Tarifário (Zonas onde você tem preços)',
        selectZonePlaceholder: '-- Selecione Zona de Origem ➔ Zona de Destino --',
        step1Help: 'Isso vincula a rota diretamente às suas tarifas (Ida e Volta) para que você possa simular e atualizar seus preços pelo Sentinela.',
        routeShortName: 'Nome Curto da Rota',
        countryField: 'País',
        originField: 'Endereço ou Aeroporto de Origem (como buscado na Booking.com)',
        destField: 'Endereço ou Hotel de Destino (dentro da zona selecionada)',
        paxField: 'Passageiros a Cotar',
        daysAheadField: 'Dias de Antecedência',
        pickupTimeField: 'Horário de Embarque',
        activeSwitchLabel: 'Rota ativa para monitoramento automático',
        cancelBtn: 'Cancelar',
        saveRouteBtn: '💾 Salvar Rota',
        modalDetailTitle: '📊 Detalhe da Inspeção',
        maxRoutesReachedToast: 'Você atingiu o limite máximo de 8 rotas no seu Robô Sentinela. Edite ou exclua uma rota para adicionar outra.',
        fillRequiredToast: 'Por favor preencha o Nome da Rota, Origem e Destino.',
        selectZoneToast: 'Por favor selecione o trecho das suas zonas tarifadas.',
        routeUpdatedToast: 'Rota atualizada no Robô Sentinela.',
        routeCreatedToast: 'Nova rota adicionada ao Robô Sentinela.',
        confirmDeleteRoute: 'Tem certeza de que deseja excluir esta rota do Sentinela?',
        routeDeletedToast: 'Rota excluída.',
        invalidCostToast: 'Insira um valor válido maior que 0.',
        bothDirectionsToast: 'Ida e Volta (ambos os sentidos)',
        oneWayToast: '1 sentido',
        tariffUpdatedToast: 'Tarifa atualizada em'
    },
    en: {
        headerTitle: '🤖 Sentinel Bot & Booking.com Live Price Simulator',
        routeQuota: 'Route Quota',
        maxLabel: 'max.',
        providerDesc: 'Check across your zones whether you are Winning (#1) or Losing on Booking.com. Use the Calculator next to each vehicle to simulate a new price or activate ⭐ Genius (-5% for you, while Booking drops the final passenger price by 10%) and update your Round-Trip tariff in one click.',
        adminDesc: 'Sentinel audits partner routes from 5 isolated servers every 12 hours (or on demand), matching all 7 Booking.com categories with our tariffs so you can simulate and update prices live.',
        refreshBtn: 'Refresh View',
        auditNowBtn: '⚡ Audit on Booking Now',
        auditRouteBtn: '⚡ Audit Route',
        auditingBtn: 'Queueing...',
        addRouteProviderBtn: 'Add Route',
        addRouteAdminBtn: 'New Watched Route',
        kpiRoutesProvider: 'My Routes in My Zones',
        kpiRoutesAdmin: 'Active Watched Routes',
        kpiSafeCheck: 'Auto check every 12h + Manual Trigger',
        kpiWinRate: 'Win Rate (Rank #1)',
        kpiValidChecks: 'valid checks',
        kpiWinsTitle: 'Winning #1 on Booking',
        kpiWinsSub: 'Times being the cheapest option',
        kpiLossesTitle: 'Won by Competitors',
        kpiLossesSub: 'Adjustable with the calculator',
        countryLabel: 'Country:',
        allCountries: '🌐 All',
        providerLabel: 'Partner:',
        allProviders: 'All Partners',
        emptyTitle: 'No routes configured to display in this view',
        emptyProviderDesc: 'Add up to 8 key routes in your zones (e.g., Airport ➔ Main Hotel) so the Sentinel Bot checks competitor prices and helps you win Rank #1.',
        emptyAdminDesc: 'No routes match the selected filter.',
        addFirstRouteBtn: 'Add My First Watched Route',
        tariffStretchBadge: '📍 Tariff Zone Pair:',
        catsWonSuffix: 'categories won',
        searchedOnBooking: 'Searched on Booking.com:',
        daysSuffix: 'days',
        lastCheckLabel: 'Last check:',
        inQueueLabel: '⚡ Priority queue (Next run)',
        editRouteBtn: 'Edit Route',
        pendingFirstCheckMsg: '⏳ This route is in priority queue for inspection on Booking.com. All category prices will appear here on the next Sentinel run.',
        thCategory: 'Category / Vehicle',
        thStatus: 'Current Status',
        thCompetitor: '#1 Competitor on Booking',
        thYourTariff: 'Your Current Tariff',
        thRecommendation: '🎯 Recommendation to Win #1',
        thCalculator: 'Live Price Calculator & Genius Simulator',
        thSave: 'Save Tariff',
        yourVehicle: '🚗 Your vehicle:',
        pendingBadge: '⏳ Pending check',
        winningBadge: 'WINNING #1',
        losingBadge: 'LOSING',
        noOffersBadge: 'No Offers',
        publicWebPrice: 'Public Web:',
        includesCommission: '(Includes Booking web fee)',
        costOneWay: 'One-Way Cost:',
        costReturn: 'Return Cost:',
        geniusActiveBadge: '⭐ Genius Active',
        youLabel: 'you',
        geniusInactiveBadge: 'Genius Inactive (0%)',
        bidBadge: 'Bid',
        noTariffLoaded: 'No tariff loaded for this vehicle',
        withoutGeniusLabel: 'Without Genius (Standard):',
        chargeMax: 'Charge max.',
        testBtn: 'Try',
        withGeniusLabel: '⭐ With Genius (-5% you ➔ -10% Web):',
        tariffUpTo: 'Tariff up to',
        netReceive: 'Net payout:',
        withoutLoweringPrice: '(Without lowering your price!)',
        useGeniusBtn: 'Use Genius',
        waitingNextCheck: 'Waiting for next web check to compute target',
        simulateBaseTariff: 'Simulate Your Base Tariff',
        geniusProgram: '⭐ Genius Program',
        geniusSwitchActive: 'Active (-10% Booking)',
        geniusSwitchInactive: 'Inactive (0%)',
        simWinMsg: '🟢 WITH THIS PRICE YOU WIN #1!',
        simLoseMsg: '🔴 WOULD STILL LOSE',
        simReadyMsg: '💡 Tariff Simulation Ready',
        geniusEffectTitle: 'Genius Effect:',
        geniusEffectDesc1: 'You contribute 5%',
        geniusEffectDesc2: 'and Booking drops the final passenger price by 10%.',
        lowerMorePrefix: 'Lower by',
        lowerMoreMid: 'more',
        orActivateGenius: 'or activate ⭐ Genius ',
        toWinRank1: 'to take Rank #1.',
        notEditable: 'Not editable',
        applyBothDirs: 'Apply Both Ways',
        savingBtn: 'Saving...',
        saveBothBtn: 'Save Both Ways',
        saveOneWayBtn: 'Save 1 Way',
        historyTitle: '📋 Recent Booking.com Inspection History',
        historySub: 'Log of recent checks executed by the Sentinel Bot on your routes.',
        loadingSentinel: 'Loading Sentinel data...',
        emptyHistory: 'No checks recorded for these routes yet. Sentinel Bot will populate this table on its next turn.',
        thDateTime: 'Date / Time',
        thRoute: 'Route',
        thSearchedDate: 'Searched Date',
        thResultStd: 'Result (Std)',
        thProvider1: '#1 Partner on Booking',
        thPublicWinner: 'Winning Public Price',
        thDetail: 'Detail',
        viewCategoriesBtn: 'View Categories',
        modalEditTitle: '✏️ Edit Route:',
        modalCreateTitle: '➕ Add Route to Sentinel Bot (Max 8 per partner)',
        step1SelectZones: '1. Select Your Tariff Zone Pair (Zones where you have prices)',
        selectZonePlaceholder: '-- Select Origin Zone ➔ Destination Zone --',
        step1Help: 'This links the route directly to your tariffs (Round-Trip) so you can simulate and update your prices right from Sentinel.',
        routeShortName: 'Route Short Name',
        countryField: 'Country',
        originField: 'Origin Address or Airport (as searched on Booking.com)',
        destField: 'Destination Address or Hotel (within the selected zone)',
        paxField: 'Passengers',
        daysAheadField: 'Days Ahead',
        pickupTimeField: 'Pickup Time',
        activeSwitchLabel: 'Active route for automatic monitoring',
        cancelBtn: 'Cancel',
        saveRouteBtn: '💾 Save Route',
        modalDetailTitle: '📊 Inspection Detail',
        maxRoutesReachedToast: 'You have reached the maximum of 8 routes in your Sentinel Bot. Edit or delete an existing route to add another.',
        fillRequiredToast: 'Please complete the Route Name, Origin, and Destination.',
        selectZoneToast: 'Please select a zone pair from your tariffs.',
        routeUpdatedToast: 'Route updated in Sentinel Bot.',
        routeCreatedToast: 'New route added to Sentinel Bot.',
        confirmDeleteRoute: 'Are you sure you want to delete this route from Sentinel?',
        routeDeletedToast: 'Route deleted.',
        invalidCostToast: 'Please enter a valid cost greater than 0.',
        bothDirectionsToast: 'Both Ways (Round-Trip)',
        oneWayToast: '1 Way',
        tariffUpdatedToast: 'Tariff updated in'
    }
};

const normalizeCatKey = (catStr) => {
    if (!catStr) return 'STANDARD';
    const s = String(catStr).toUpperCase().replace(/_/g, ' ').trim();
    if (s.includes('LARGE') && s.includes('PEOPLE')) return 'LARGE PEOPLE CARRIER';
    if (s.includes('EXECUTIVE') && s.includes('PEOPLE')) return 'EXECUTIVE PEOPLE CARRIER';
    if (s.includes('PEOPLE') || s.includes('VAN')) return 'PEOPLE CARRIER';
    if (s.includes('MINIBUS') || s.includes('SPRINTER') || s.includes('BUS')) return 'MINIBUS';
    if (s.includes('EXECUTIVE')) return 'EXECUTIVE';
    if (s.includes('LUXURY')) return 'LUXURY';
    return 'STANDARD';
};

const BookingSentinelView = ({
    providerId = null,
    providerName = null,
    providerTariffs = [],
    onTariffUpdated = null
}) => {
    const isProviderMode = Boolean(providerId);
    const { language } = useLanguage();
    const L = SENTINEL_I18N[language] || SENTINEL_I18N.es;

    const [routes, setRoutes] = useState([]);
    const [logsData, setLogsData] = useState({
        summary: { total_checks: 0, valid_checks: 0, wins: 0, losses: 0, win_rate: 0 },
        logs: []
    });
    const [loading, setLoading] = useState(true);
    const [selectedCountry, setSelectedCountry] = useState('');
    const [selectedProviderFilter, setSelectedProviderFilter] = useState('');
    const [expandedRouteIds, setExpandedRouteIds] = useState({});
    const [showRouteModal, setShowRouteModal] = useState(false);
    const [editingRoute, setEditingRoute] = useState(null);
    const [savingRoute, setSavingRoute] = useState(false);
    const [selectedLogDetails, setSelectedLogDetails] = useState(null);

    // Estado de simulación por fila: key = `${routeId}_${catKey}`
    const [simState, setSimState] = useState({});
    const [savingSimKey, setSavingSimKey] = useState(null);

    const [formData, setFormData] = useState({
        name: '',
        country: 'Chile',
        origin_text: '',
        destination_text: '',
        passengers: 2,
        days_ahead: 3,
        pickup_time: '12:00',
        is_active: true,
        origin_zone_id: '',
        destination_zone_id: '',
        zone_pair_key: ''
    });

    // Pares de zonas únicos donde el proveedor tiene tarifas configuradas
    const providerZonePairs = useMemo(() => {
        if (!Array.isArray(providerTariffs) || providerTariffs.length === 0) return [];
        const map = new Map();
        providerTariffs.forEach(t => {
            const oz = t.origin_zone;
            const dz = t.destination_zone;
            if (!oz?.id || !dz?.id) return;
            const key = `${oz.id}_${dz.id}`;
            if (!map.has(key)) {
                const rawCodes = [
                    oz.base?.country_code,
                    dz.base?.country_code,
                    oz.country_code !== 'CL' ? oz.country_code : null,
                    dz.country_code !== 'CL' ? dz.country_code : null,
                    oz.base?.country,
                    dz.base?.country
                ].filter(Boolean).map(c => String(c).trim().toUpperCase());

                const currCodes = [
                    oz.base?.currency_code,
                    dz.base?.currency_code
                ].filter(Boolean).map(c => String(c).trim().toUpperCase());

                const combinedNames = `${oz.base?.name || ''} ${dz.base?.name || ''} ${oz.name || ''} ${dz.name || ''}`.toLowerCase();

                let normCountry = 'Chile';
                if (
                    rawCodes.some(c => c === 'DO' || c === 'DOM' || c.includes('DOMINIC')) ||
                    currCodes.includes('DOP') ||
                    combinedNames.includes('dominicana') ||
                    combinedNames.includes('punta cana') ||
                    combinedNames.includes('santo domingo') ||
                    /\b(puj|sdq)\b/.test(combinedNames)
                ) {
                    normCountry = 'Dominican Republic';
                } else if (
                    rawCodes.some(c => c === 'BR' || c === 'BRA' || c.includes('BRASIL') || c.includes('BRAZIL')) ||
                    currCodes.includes('BRL') ||
                    combinedNames.includes('brasil') ||
                    combinedNames.includes('sao paulo') ||
                    combinedNames.includes('são paulo') ||
                    combinedNames.includes('rio de janeiro')
                ) {
                    normCountry = 'Brasil';
                } else if (
                    rawCodes.some(c => c === 'PE' || c === 'PER' || c.includes('PERU') || c.includes('PERÚ')) ||
                    currCodes.includes('PEN') ||
                    combinedNames.includes('lima') ||
                    combinedNames.includes('peru') ||
                    combinedNames.includes('perú')
                ) {
                    normCountry = 'Peru';
                } else if (
                    rawCodes.some(c => c === 'AR' || c === 'ARG' || c.includes('ARGENTIN')) ||
                    currCodes.includes('ARS') ||
                    combinedNames.includes('argentina') ||
                    combinedNames.includes('buenos aires') ||
                    combinedNames.includes('mendoza')
                ) {
                    normCountry = 'Argentina';
                }

                map.set(key, {
                    key,
                    origin_zone_id: oz.id,
                    origin_zone_name: oz.name,
                    destination_zone_id: dz.id,
                    destination_zone_name: dz.name,
                    country: normCountry,
                    label: `${oz.name} ➔ ${dz.name}`
                });
            }
        });
        return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
    }, [providerTariffs]);

    const loadAllData = useCallback(async () => {
        setLoading(true);
        try {
            const routeParams = {};
            const logParams = { limit: 60 };
            if (providerId) {
                routeParams.provider_id = providerId;
                logParams.provider_id = providerId;
            }
            if (selectedCountry) {
                logParams.country = selectedCountry;
            }

            const [routesRes, logsRes] = await Promise.all([
                getSentinelRoutes(routeParams),
                getSentinelLogs(logParams)
            ]);
            const loadedRoutes = routesRes || [];
            setRoutes(loadedRoutes);
            setLogsData(logsRes || { summary: { total_checks: 0, valid_checks: 0, wins: 0, losses: 0, win_rate: 0 }, logs: [] });

            // Expandir todas las rutas por defecto para ver la hoja completa de inmediato
            setExpandedRouteIds(prev => {
                const next = { ...prev };
                loadedRoutes.forEach(r => {
                    if (next[r.id] === undefined) next[r.id] = true;
                });
                return next;
            });

            // Inicializar estado del simulador con los valores actuales de cada tarifa
            setSimState(prev => {
                const next = { ...prev };
                loadedRoutes.forEach(r => {
                    const details = Array.isArray(r.last_details) ? r.last_details : [];
                    const tariffsMap = r.tariffs_by_category || {};
                    const allCats = new Set([
                        ...details.map(d => normalizeCatKey(d.category || d.title)),
                        ...Object.keys(tariffsMap)
                    ]);
                    allCats.forEach(catKey => {
                        const simKey = `${r.id}_${catKey}`;
                        const item = details.find(d => normalizeCatKey(d.category || d.title) === catKey);
                        const mt = item?.matched_tariff || tariffsMap[catKey];
                        if (mt && !next[simKey]) {
                            next[simKey] = {
                                cost_local: mt.cost_local ?? 0,
                                bid_discount: mt.booking_bid_discount ?? 0,
                                genius_active: (mt.booking_genius_discount || 0) >= 5,
                                genius_discount: (mt.booking_genius_discount || 0) >= 5 ? mt.booking_genius_discount : 5,
                                both_directions: true
                            };
                        }
                    });
                });
                return next;
            });
        } catch (err) {
            console.error('Error cargando datos del Centinela:', err);
        } finally {
            setLoading(false);
        }
    }, [providerId, selectedCountry]);

    useEffect(() => {
        loadAllData();
    }, [loadAllData]);

    // Conteo de rutas propias del proveedor (límite máx 8)
    const providerOwnRoutesCount = useMemo(() => {
        if (!providerId) return routes.length;
        return routes.filter(r => r.provider_id === providerId).length;
    }, [routes, providerId]);

    const [triggeringAuditId, setTriggeringAuditId] = useState(null);

    const handleTriggerAudit = async (targetRouteId = null) => {
        const key = targetRouteId ? `route_${targetRouteId}` : 'all';
        setTriggeringAuditId(key);
        try {
            const payload = {};
            if (targetRouteId) payload.route_id = targetRouteId;
            if (providerId) payload.provider_id = providerId;
            const res = await triggerSentinelCheck(payload);
            toast.success(res?.message || '⚡ Rutas encoladas con prioridad #1 para auditoría en Booking.com.');
            await loadAllData();
        } catch (err) {
            console.error(err);
        } finally {
            setTriggeringAuditId(null);
        }
    };

    const handleOpenCreate = () => {
        if (isProviderMode && providerOwnRoutesCount >= MAX_ROUTES_PER_PROVIDER) {
            toast.warning(L.maxRoutesReachedToast);
            return;
        }
        setEditingRoute(null);
        const defaultPair = providerZonePairs[0] || null;
        const defaultOriginAirport = defaultPair ? getSuggestedAirportForZone(defaultPair.origin_zone_name) : null;
        const defaultDestAirport = defaultPair ? getSuggestedAirportForZone(defaultPair.destination_zone_name) : null;
        setFormData({
            name: defaultPair ? `${defaultPair.origin_zone_name} ➔ ${defaultPair.destination_zone_name}` : '',
            country: defaultPair ? defaultPair.country : 'Chile',
            origin_text: defaultOriginAirport || '',
            destination_text: defaultDestAirport || '',
            passengers: 2,
            days_ahead: 3,
            pickup_time: '12:00',
            is_active: true,
            origin_zone_id: defaultPair ? defaultPair.origin_zone_id : '',
            destination_zone_id: defaultPair ? defaultPair.destination_zone_id : '',
            zone_pair_key: defaultPair ? defaultPair.key : ''
        });
        setShowRouteModal(true);
    };

    const handleOpenEdit = (route) => {
        setEditingRoute(route);
        const pairKey = (route.origin_zone_id && route.destination_zone_id)
            ? `${route.origin_zone_id}_${route.destination_zone_id}`
            : '';
        setFormData({
            name: route.name || '',
            country: route.country || 'Chile',
            origin_text: route.origin_text || '',
            destination_text: route.destination_text || '',
            passengers: route.passengers || 2,
            days_ahead: route.days_ahead || 3,
            pickup_time: route.pickup_time || '12:00',
            is_active: route.is_active !== false,
            origin_zone_id: route.origin_zone_id || '',
            destination_zone_id: route.destination_zone_id || '',
            zone_pair_key: pairKey
        });
        setShowRouteModal(true);
    };

    const handleZonePairSelect = (pairKey) => {
        const pair = providerZonePairs.find(p => p.key === pairKey);
        if (!pair) {
            setFormData(prev => ({ ...prev, zone_pair_key: '', origin_zone_id: '', destination_zone_id: '' }));
            return;
        }
        const suggOriginAirport = getSuggestedAirportForZone(pair.origin_zone_name);
        const suggDestAirport = getSuggestedAirportForZone(pair.destination_zone_name);
        setFormData(prev => ({
            ...prev,
            zone_pair_key: pair.key,
            origin_zone_id: pair.origin_zone_id,
            destination_zone_id: pair.destination_zone_id,
            country: pair.country,
            name: !editingRoute ? `${pair.origin_zone_name} ➔ ${pair.destination_zone_name}` : (prev.name || `${pair.origin_zone_name} ➔ ${pair.destination_zone_name}`),
            origin_text: !editingRoute ? (suggOriginAirport || '') : (prev.origin_text || suggOriginAirport || ''),
            destination_text: !editingRoute ? (suggDestAirport || '') : (prev.destination_text || suggDestAirport || '')
        }));
    };

    const handleSaveRoute = async (e) => {
        e.preventDefault();
        if (!formData.name.trim() || !formData.origin_text.trim() || !formData.destination_text.trim()) {
            toast.warning(L.fillRequiredToast);
            return;
        }
        if (isProviderMode && providerZonePairs.length > 0 && (!formData.origin_zone_id || !formData.destination_zone_id)) {
            toast.warning(L.selectZoneToast);
            return;
        }

        setSavingRoute(true);
        try {
            const payload = {
                name: formData.name.trim(),
                country: formData.country,
                origin_text: formData.origin_text.trim(),
                destination_text: formData.destination_text.trim(),
                passengers: formData.passengers,
                days_ahead: formData.days_ahead,
                pickup_time: formData.pickup_time,
                is_active: formData.is_active,
                provider_id: providerId || (editingRoute ? editingRoute.provider_id : null),
                origin_zone_id: formData.origin_zone_id ? Number(formData.origin_zone_id) : null,
                destination_zone_id: formData.destination_zone_id ? Number(formData.destination_zone_id) : null
            };

            if (editingRoute) {
                await updateSentinelRoute(editingRoute.id, payload);
                toast.success(L.routeUpdatedToast);
            } else {
                await createSentinelRoute(payload);
                toast.success(L.routeCreatedToast);
            }
            setShowRouteModal(false);
            await loadAllData();
        } catch (err) {
            console.error(err);
        } finally {
            setSavingRoute(false);
        }
    };

    const handleToggleActive = async (route) => {
        try {
            await updateSentinelRoute(route.id, { is_active: !route.is_active });
            await loadAllData();
        } catch (err) {
            console.error(err);
        }
    };

    const handleDeleteRoute = async (route) => {
        if (!window.confirm(`${L.confirmDeleteRoute} ("${route.name}")`)) return;
        try {
            await deleteSentinelRoute(route.id);
            toast.info(L.routeDeletedToast);
            await loadAllData();
        } catch (err) {
            console.error(err);
        }
    };

    const formatMoney = (amount, currency = 'CLP') => {
        if (amount === null || amount === undefined || isNaN(Number(amount))) return '—';
        const sym = CURRENCY_SYMBOLS[currency] || '$';
        const localeStr = language === 'pt' ? 'pt-BR' : (language === 'en' ? 'en-US' : 'es-CL');
        if (currency === 'CLP' || currency === 'ARS' || currency === 'DOP') {
            return `${sym}${Math.round(Number(amount)).toLocaleString(localeStr)} ${currency}`;
        }
        return `${sym} ${Number(amount).toLocaleString(localeStr, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
    };

    const formatDateTime = (isoStr) => {
        if (!isoStr) return L.inQueueLabel;
        try {
            const d = new Date(isoStr);
            const localeStr = language === 'pt' ? 'pt-BR' : (language === 'en' ? 'en-US' : 'es-CL');
            return d.toLocaleString(localeStr, {
                day: '2-digit',
                month: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return isoStr;
        }
    };

    const updateRowSim = (simKey, patch, fallbackTariff = null) => {
        setSimState(prev => {
            const current = prev[simKey] || {
                cost_local: fallbackTariff?.cost_local ?? 0,
                bid_discount: fallbackTariff?.booking_bid_discount ?? 0,
                genius_active: (fallbackTariff?.booking_genius_discount || 0) >= 5,
                genius_discount: (fallbackTariff?.booking_genius_discount || 0) >= 5 ? fallbackTariff.booking_genius_discount : 5,
                both_directions: true
            };
            return {
                ...prev,
                [simKey]: { ...current, ...patch }
            };
        });
    };

    /**
     * Motor matemático de simulación en vivo por categoría:
     * Calcula cómo cambia el precio público en Booking.com según:
     * 1) El Costo Base del Proveedor (cost_local)
     * 2) El % Descuento de Puja (bid_discount)
     * 3) ⭐ Genius Activo: El proveedor y EmiGo bajan un 5%, pero Booking.com muestra un 10% de descuento al pasajero final!
     */
    const computeCategorySimulation = (item, matchedTariff, simRow) => {
        if (!matchedTariff) return null;

        const curr = matchedTariff.currency_code || 'USD';
        const rateToClp = Number(matchedTariff.rate_to_clp || 1.0) || 1.0;
        const marginPct = Number(matchedTariff.margin_pct ?? 20.0);
        const marginDenom = Math.max(0.05, 1.0 - (marginPct / 100.0));

        const origCostLocal = Number(matchedTariff.cost_local || 0);
        const origBidDisc = Number(matchedTariff.booking_bid_discount || 0);
        const origGeniusDisc = Number(matchedTariff.booking_genius_discount || 0);
        const origGeniusActive = origGeniusDisc >= 5;

        const simCostLocal = simRow?.cost_local !== undefined && simRow?.cost_local !== '' ? Number(simRow.cost_local) : origCostLocal;
        const simBidDisc = simRow?.bid_discount !== undefined && simRow?.bid_discount !== '' ? Number(simRow.bid_discount) : origBidDisc;
        const simGeniusActive = simRow?.genius_active !== undefined ? Boolean(simRow.genius_active) : origGeniusActive;
        const simGeniusPct = simGeniusActive ? Number(simRow?.genius_discount || 5) : 0;

        // En Booking.com, cuando el proveedor aporta 5% Genius compartido, Booking aporta el otro 5% -> 10% descuento público en la web
        const bookingPublicGeniusDropPct = simGeniusActive ? Math.min(10, simGeniusPct * 2) : 0;
        const origPublicGeniusDropPct = origGeniusActive ? Math.min(10, origGeniusDisc * 2) : 0;

        // Lo que recibe neto el proveedor en su bolsillo
        const providerNetCurrent = origCostLocal * (1.0 - origBidDisc / 100.0) * (1.0 - (origGeniusActive ? origGeniusDisc : 0) / 100.0);
        const providerNetSimulated = simCostLocal * (1.0 - simBidDisc / 100.0) * (1.0 - simGeniusPct / 100.0);

        // Precio API EmiGo en CLP (antes de comisión web de Booking)
        const currentApiClp = (origCostLocal * rateToClp / marginDenom) * (1.0 - origBidDisc / 100.0);
        const simApiClp = (simCostLocal * rateToClp / marginDenom) * (1.0 - simBidDisc / 100.0);

        // Precio público del ganador #1 en Booking.com (capturado por el Bot Centinela en CLP)
        const webWinnerClp = item?.price ? Number(item.price) : null;
        const isCurrentlyWinning = Boolean(item?.is_winner);

        // Factor real de markup de Booking en esta ruta (~1.20 por defecto, o calibrado si somos el ganador actual)
        let bookingWebMarkup = 1.20;
        if (isCurrentlyWinning && webWinnerClp && currentApiClp > 0) {
            const origGeniusFactor = 1.0 - (origPublicGeniusDropPct / 100.0);
            const calibrated = webWinnerClp / (currentApiClp * origGeniusFactor);
            if (calibrated >= 1.02 && calibrated <= 1.45) {
                bookingWebMarkup = calibrated;
            }
        }

        // Precio público estimado de nuestra oferta en la web de Booking (en CLP y en moneda del proveedor)
        const estOurPublicSimClp = simApiClp * bookingWebMarkup * (1.0 - bookingPublicGeniusDropPct / 100.0);

        const webWinnerInProviderCurr = webWinnerClp ? (webWinnerClp / rateToClp) : null;
        const estOurPublicSimInProviderCurr = estOurPublicSimClp / rateToClp;

        // ¿Cuánto tendría que cobrar el proveedor (Costo Base) para GANAR #1 (quedar 1% abajo del ganador)?
        let targetCostNoGenius = null;
        let targetCostWithGenius = null;
        let targetNetWithGenius = null;

        if (webWinnerClp && webWinnerClp > 0) {
            const targetPublicClp = isCurrentlyWinning ? webWinnerClp : (webWinnerClp * 0.99);
            const bidFactor = Math.max(0.1, 1.0 - simBidDisc / 100.0);

            // 1) Costo máximo para ganar SIN Genius:
            const maxApiNoGeniusClp = targetPublicClp / bookingWebMarkup;
            const rawCostNoGenius = (maxApiNoGeniusClp * marginDenom) / (rateToClp * bidFactor);

            // 2) Costo máximo para ganar ACTIVANDO Genius (-10% en Booking web):
            const maxApiWithGeniusClp = targetPublicClp / (bookingWebMarkup * 0.90);
            const rawCostWithGenius = (maxApiWithGeniusClp * marginDenom) / (rateToClp * bidFactor);

            const roundPrice = (val) => (curr === 'CLP' || curr === 'ARS' || curr === 'DOP') ? Math.floor(val) : Math.floor(val * 100) / 100;
            targetCostNoGenius = roundPrice(rawCostNoGenius);
            targetCostWithGenius = roundPrice(rawCostWithGenius);
            targetNetWithGenius = roundPrice(targetCostWithGenius * bidFactor * 0.95);
        }

        // Determinar si con los valores puestos en el simulador GANA #1 o PIERDE
        let wouldWin = isCurrentlyWinning;
        let gapInProviderCurr = 0;
        if (webWinnerClp && webWinnerClp > 0) {
            if (isCurrentlyWinning) {
                wouldWin = estOurPublicSimClp <= webWinnerClp * 1.002;
            } else {
                wouldWin = estOurPublicSimClp <= webWinnerClp * 0.998;
            }
            const referenceTargetCost = simGeniusActive ? targetCostWithGenius : targetCostNoGenius;
            if (referenceTargetCost !== null) {
                gapInProviderCurr = simCostLocal - referenceTargetCost;
            }
        }

        const hasChanges = (
            Math.abs(simCostLocal - origCostLocal) > 0.001 ||
            Math.abs(simBidDisc - origBidDisc) > 0.001 ||
            simGeniusActive !== origGeniusActive
        );

        const canRaiseOrKeepWithGenius = (
            !isCurrentlyWinning &&
            targetCostWithGenius !== null &&
            targetCostWithGenius >= origCostLocal
        );

        return {
            curr,
            origCostLocal,
            origBidDisc,
            origGeniusActive,
            simCostLocal,
            simBidDisc,
            simGeniusActive,
            simGeniusPct,
            bookingPublicGeniusDropPct,
            providerNetCurrent,
            providerNetSimulated,
            webWinnerClp,
            webWinnerInProviderCurr,
            estOurPublicSimInProviderCurr,
            targetCostNoGenius,
            targetCostWithGenius,
            targetNetWithGenius,
            wouldWin,
            gapInProviderCurr,
            hasChanges,
            canRaiseOrKeepWithGenius
        };
    };

    const handleApplySimulatedTariff = async (route, catKey, matchedTariff) => {
        if (!matchedTariff?.tariff_id) return;
        const simKey = `${route.id}_${catKey}`;
        const simRow = simState[simKey] || {
            cost_local: matchedTariff.cost_local,
            bid_discount: matchedTariff.booking_bid_discount || 0,
            genius_active: (matchedTariff.booking_genius_discount || 0) >= 5,
            genius_discount: 5,
            both_directions: true
        };

        const costVal = Number(simRow.cost_local);
        if (isNaN(costVal) || costVal <= 0) {
            toast.warning(L.invalidCostToast);
            return;
        }

        setSavingSimKey(simKey);
        try {
            const payload = {
                tariff_id: matchedTariff.tariff_id,
                cost_local: costVal,
                booking_bid_discount: Number(simRow.bid_discount || 0),
                booking_genius_discount: simRow.genius_active ? Number(simRow.genius_discount || 5) : 0,
                apply_both_directions: Boolean(simRow.both_directions),
                route_id: route.id
            };

            const res = await applySentinelTariff(payload);
            const dirText = res.directions_updated > 1 ? L.bothDirectionsToast : L.oneWayToast;
            const geniusText = payload.booking_genius_discount >= 5 ? ' + ⭐ Genius (-10% Booking)' : '';
            toast.success(`✅ ${L.tariffUpdatedToast} ${dirText}${geniusText}.`);

            await loadAllData();
            if (typeof onTariffUpdated === 'function') {
                onTariffUpdated();
            }
        } catch (err) {
            console.error('Error aplicando tarifa desde el Centinela:', err);
        } finally {
            setSavingSimKey(null);
        }
    };

    const renderStatusBadge = (status, isWinner) => {
        if (!status) return <Badge key="st-pending" bg="secondary"><span>{L.pendingBadge}</span></Badge>;
        if (status === 'WINNING' || isWinner) {
            return <Badge key="st-win" bg="success" className="px-2 py-1"><CheckCircle size={12} className="me-1" /><span>{L.winningBadge}</span></Badge>;
        }
        if (status === 'LOSING') {
            return <Badge key="st-lose" bg="danger" className="px-2 py-1"><XCircle size={12} className="me-1" /><span>{L.losingBadge}</span></Badge>;
        }
        if (status === 'NO_RESULTS') {
            return <Badge key="st-nores" bg="warning" text="dark" className="px-2 py-1"><AlertTriangle size={12} className="me-1" /><span>{L.noOffersBadge}</span></Badge>;
        }
        return <Badge key="st-other" bg="secondary"><span>{status}</span></Badge>;
    };

    // Opciones de proveedores detectados en las rutas (para filtro Admin)
    const availableProviders = useMemo(() => {
        const map = new Map();
        routes.forEach(r => {
            if (r.provider_id && r.provider_name) {
                map.set(r.provider_id, r.provider_name);
            }
            Object.values(r.tariffs_by_category || {}).forEach(t => {
                if (t.provider_id && t.provider_name) {
                    map.set(t.provider_id, t.provider_name);
                }
            });
        });
        return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
    }, [routes]);

    const filteredRoutes = useMemo(() => {
        return routes.filter(r => {
            if (selectedCountry && (r.country || '').toLowerCase() !== selectedCountry.toLowerCase()) {
                return false;
            }
            if (!isProviderMode && selectedProviderFilter) {
                const pid = Number(selectedProviderFilter);
                const hasProvTariff = Object.values(r.tariffs_by_category || {}).some(t => t.provider_id === pid);
                if (r.provider_id !== pid && !hasProvTariff) return false;
            }
            return true;
        });
    }, [routes, selectedCountry, isProviderMode, selectedProviderFilter]);

    const summary = logsData?.summary || { total_checks: 0, valid_checks: 0, wins: 0, losses: 0, win_rate: 0 };

    return (
        <div className="notranslate" translate="no">
            {/* CABECERA INFORMATIVA Y ACCIONES */}
            <Alert variant="info" className="d-flex flex-column flex-md-row justify-content-between align-items-md-center shadow-sm border-0 mb-4 gap-3">
                <div>
                    <h5 className="fw-bold mb-1 d-flex align-items-center gap-2">
                        <span>{L.headerTitle}</span>
                        {isProviderMode && (
                            <Badge bg="dark" className="ms-1">
                                <span>{L.routeQuota}: {providerOwnRoutesCount} / {MAX_ROUTES_PER_PROVIDER} {L.maxLabel}</span>
                            </Badge>
                        )}
                    </h5>
                    <div className="small text-muted">
                        <span>{isProviderMode ? L.providerDesc : L.adminDesc}</span>
                    </div>
                </div>
                <div className="d-flex flex-wrap gap-2 flex-shrink-0">
                    <Button
                        variant="warning"
                        size="sm"
                        className="fw-bold text-nowrap text-dark border border-warning shadow-sm"
                        onClick={() => handleTriggerAudit(null)}
                        disabled={loading || triggeringAuditId !== null}
                    >
                        {triggeringAuditId === 'all' ? (
                            <>
                                <Spinner size="sm" animation="border" className="me-1" />
                                <span>{L.auditingBtn}</span>
                            </>
                        ) : (
                            <span>{L.auditNowBtn}</span>
                        )}
                    </Button>
                    <Button variant="outline-primary" size="sm" className="fw-bold text-nowrap" onClick={loadAllData} disabled={loading}>
                        <RefreshCw size={14} className={`me-1 ${loading ? 'spin' : ''}`} />
                        <span>{L.refreshBtn}</span>
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        className="fw-bold text-nowrap"
                        onClick={handleOpenCreate}
                        disabled={isProviderMode && providerOwnRoutesCount >= MAX_ROUTES_PER_PROVIDER}
                    >
                        <Plus size={14} className="me-1" />
                        <span>{isProviderMode ? `${L.addRouteProviderBtn} (${providerOwnRoutesCount}/${MAX_ROUTES_PER_PROVIDER})` : L.addRouteAdminBtn}</span>
                    </Button>
                </div>
            </Alert>

            {/* KPIs DE COMPETITIVIDAD */}
            <Row className="mb-4 g-3">
                <Col xs={6} md={3}>
                    <Card className="shadow-sm border-0 h-100">
                        <Card.Body className="text-center py-3">
                            <div className="text-muted small fw-bold text-uppercase">
                                <span>{isProviderMode ? L.kpiRoutesProvider : L.kpiRoutesAdmin}</span>
                            </div>
                            <h2 className="fw-bold text-primary my-1">
                                <span>{filteredRoutes.filter(r => r.is_active).length} </span>
                                <span className="fs-6 text-muted">/ {isProviderMode ? MAX_ROUTES_PER_PROVIDER : filteredRoutes.length}</span>
                            </h2>
                            <div className="small text-muted"><span>{L.kpiSafeCheck}</span></div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col xs={6} md={3}>
                    <Card className="shadow-sm border-0 h-100">
                        <Card.Body className="text-center py-3">
                            <div className="text-muted small fw-bold text-uppercase">{L.kpiWinRate}</div>
                            <h2 className={`fw-bold my-1 ${summary.win_rate >= 50 ? 'text-success' : 'text-danger'}`}>
                                {summary.win_rate}%
                            </h2>
                            <div className="small text-muted">{summary.valid_checks} {L.kpiValidChecks}</div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col xs={6} md={3}>
                    <Card className="shadow-sm border-0 h-100 border-start border-success border-4">
                        <Card.Body className="text-center py-3">
                            <div className="text-muted small fw-bold text-uppercase">{L.kpiWinsTitle}</div>
                            <h2 className="fw-bold text-success my-1">{summary.wins}</h2>
                            <div className="small text-muted">{L.kpiWinsSub}</div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col xs={6} md={3}>
                    <Card className="shadow-sm border-0 h-100 border-start border-danger border-4">
                        <Card.Body className="text-center py-3">
                            <div className="text-muted small fw-bold text-uppercase">{L.kpiLossesTitle}</div>
                            <h2 className="fw-bold text-danger my-1">{summary.losses}</h2>
                            <div className="small text-muted">{L.kpiLossesSub}</div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* BARRA DE FILTROS (SOLO ADMIN) */}
            {!isProviderMode && (
                <Card className="shadow-sm border-0 mb-3">
                    <Card.Body className="py-2 px-3 d-flex flex-wrap justify-content-between align-items-center gap-2">
                        <div className="d-flex flex-wrap gap-1 align-items-center">
                            <span className="small fw-bold text-muted me-2">{L.countryLabel}</span>
                            {['', 'Chile', 'Brasil', 'Peru', 'Argentina', 'Dominican Republic'].map(c => (
                                <Button
                                    key={c || 'all'}
                                    size="sm"
                                    variant={selectedCountry === c ? 'dark' : 'outline-secondary'}
                                    onClick={() => setSelectedCountry(c)}
                                    className="fw-bold py-1"
                                >
                                    {c === '' ? L.allCountries : (COUNTRY_FLAGS[c] || c)}
                                </Button>
                            ))}
                        </div>
                        {availableProviders.length > 0 && (
                            <div className="d-flex align-items-center gap-2">
                                <span className="small fw-bold text-muted">{L.providerLabel}</span>
                                <Form.Select
                                    size="sm"
                                    style={{ width: '220px' }}
                                    value={selectedProviderFilter}
                                    onChange={(e) => setSelectedProviderFilter(e.target.value)}
                                >
                                    <option value="">{L.allProviders}</option>
                                    {availableProviders.map(p => (
                                        <option key={p.id} value={p.id}>{p.name}</option>
                                    ))}
                                </Form.Select>
                            </div>
                        )}
                    </Card.Body>
                </Card>
            )}

            {/* HOJA COMPLETA POR RUTA: TABLA COMPARATIVA + CALCULADORA INTERACTIVA + GENIUS */}
            {filteredRoutes.length === 0 ? (
                <Card key="sentinel-routes-empty" className="shadow-sm border-0 mb-4">
                    <Card.Body className="text-center py-5 text-muted">
                        <h6 className="fw-bold text-dark mb-2"><span>{L.emptyTitle}</span></h6>
                        <p className="small mb-3">
                            <span>{isProviderMode ? L.emptyProviderDesc : L.emptyAdminDesc}</span>
                        </p>
                        <Button variant="primary" size="sm" className="fw-bold" onClick={handleOpenCreate}>
                            <Plus size={14} className="me-1" />
                            <span>{L.addFirstRouteBtn}</span>
                        </Button>
                    </Card.Body>
                </Card>
            ) : (
                <div key="sentinel-routes-list">
                {filteredRoutes.map(route => {
                    const isExpanded = expandedRouteIds[route.id] !== false;
                    const rawDetails = Array.isArray(route.last_details) ? route.last_details : [];
                    const tariffsByCat = route.tariffs_by_category || {};

                    const categoryOrder = [
                        'STANDARD',
                        'EXECUTIVE',
                        'PEOPLE CARRIER',
                        'LARGE PEOPLE CARRIER',
                        'EXECUTIVE PEOPLE CARRIER',
                        'MINIBUS',
                        'LUXURY'
                    ];
                    const mergedCategories = [];
                    const seenSet = new Set();

                    rawDetails.forEach(item => {
                        const catKey = normalizeCatKey(item.category || item.title);
                        if (!seenSet.has(catKey)) {
                            seenSet.add(catKey);
                            mergedCategories.push({
                                catKey,
                                item,
                                matchedTariff: item.matched_tariff || tariffsByCat[catKey] || null
                            });
                        }
                    });

                    categoryOrder.forEach(catKey => {
                        if (!seenSet.has(catKey) && tariffsByCat[catKey]) {
                            seenSet.add(catKey);
                            mergedCategories.push({
                                catKey,
                                item: null,
                                matchedTariff: tariffsByCat[catKey]
                            });
                        }
                    });

                    const winsCount = rawDetails.filter(c => c.is_winner).length;
                    const totalWebCats = rawDetails.length;

                    return (
                        <Card key={route.id} className={`shadow-sm border-0 mb-4 ${!route.is_active ? 'opacity-75' : ''}`}>
                            <Card.Header className="bg-white py-3 border-bottom">
                                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                                    <div className="d-flex align-items-center gap-3 flex-wrap">
                                        <Form.Check
                                            type="switch"
                                            checked={route.is_active}
                                            onChange={() => handleToggleActive(route)}
                                        />
                                        <div>
                                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                                <h6 className="fw-bold text-dark mb-0"><span>{route.name}</span></h6>
                                                <Badge bg="light" text="dark" className="border">
                                                    <span>{COUNTRY_FLAGS[route.country] || route.country}</span>
                                                </Badge>
                                                {route.origin_zone_name && route.destination_zone_name && (
                                                    <Badge bg="primary" className="bg-opacity-10 text-primary border border-primary border-opacity-25">
                                                        <span>{L.tariffStretchBadge} {route.origin_zone_name} ↔ {route.destination_zone_name}</span>
                                                    </Badge>
                                                )}
                                                {renderStatusBadge(route.last_status, route.last_status === 'WINNING')}
                                                {totalWebCats > 0 && (
                                                    <Badge bg={winsCount > 0 ? 'success' : 'secondary'} className="px-2">
                                                        <span>🏆 {winsCount} / {totalWebCats} {L.catsWonSuffix}</span>
                                                    </Badge>
                                                )}
                                            </div>
                                            <div className="small text-muted mt-1">
                                                <strong>{L.searchedOnBooking}</strong> <span>{route.origin_text} ➔ {route.destination_text}</span>
                                                <span className="ms-2 badge bg-light text-dark border">👥 {route.passengers} pax</span>
                                                <span className="ms-1 badge bg-light text-dark border">📅 +{route.days_ahead} {L.daysSuffix} ({route.pickup_time})</span>
                                                <span className="ms-2 text-muted">
                                                    <Clock size={12} className="me-1" />
                                                    <span>{L.lastCheckLabel} </span>
                                                    <strong>{formatDateTime(route.last_checked_at)}</strong>
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <Button
                                            variant="outline-warning"
                                            size="sm"
                                            className="fw-bold text-dark border-warning"
                                            onClick={() => handleTriggerAudit(route.id)}
                                            disabled={triggeringAuditId !== null}
                                        >
                                            {triggeringAuditId === `route_${route.id}` ? (
                                                <>
                                                    <Spinner size="sm" animation="border" className="me-1" />
                                                    <span>{L.auditingBtn}</span>
                                                </>
                                            ) : (
                                                <span>{L.auditRouteBtn}</span>
                                            )}
                                        </Button>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() => handleOpenEdit(route)}
                                        >
                                            <Edit size={14} className="me-1" />
                                            <span>{L.editRouteBtn}</span>
                                        </Button>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => handleDeleteRoute(route)}
                                        >
                                            <Trash2 size={14} />
                                        </Button>
                                        <Button
                                            variant="light"
                                            size="sm"
                                            className="border fw-bold"
                                            onClick={() => setExpandedRouteIds(prev => ({ ...prev, [route.id]: !isExpanded }))}
                                        >
                                            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                        </Button>
                                    </div>
                                </div>
                            </Card.Header>

                            {isExpanded && (
                                <Card.Body className="p-0">
                                    {mergedCategories.length === 0 ? (
                                        <div key="sentinel-cats-empty" className="text-center py-4 text-muted small">
                                            <span>{L.pendingFirstCheckMsg}</span>
                                        </div>
                                    ) : (
                                        <div key="sentinel-cats-list">
                                            {/* VISTA MÓVIL (CELULARES): TARJETAS VERTICALES TÁCTILES SIN SCROLL HORIZONTAL */}
                                            <div className="d-md-none p-2 bg-light d-flex flex-column gap-3">
                                                {mergedCategories.map(({ catKey, item, matchedTariff }) => {
                                                    const simKey = `${route.id}_${catKey}`;
                                                    const simRow = simState[simKey] || {
                                                        cost_local: matchedTariff?.cost_local ?? '',
                                                        bid_discount: matchedTariff?.booking_bid_discount ?? 0,
                                                        genius_active: (matchedTariff?.booking_genius_discount || 0) >= 5,
                                                        genius_discount: (matchedTariff?.booking_genius_discount || 0) >= 5 ? matchedTariff.booking_genius_discount : 5,
                                                        both_directions: true
                                                    };

                                                    const simResult = computeCategorySimulation(item, matchedTariff, simRow);
                                                    const isWin = Boolean(item?.is_winner);
                                                    const isSavingThis = savingSimKey === simKey;
                                                    const localCurr = matchedTariff?.currency_code || route.last_currency || 'USD';
                                                    const sym = CURRENCY_SYMBOLS[localCurr] || '$';

                                                    return (
                                                        <div
                                                            key={`mob_${catKey}`}
                                                            className={`bg-white rounded-3 shadow-sm border p-3 ${
                                                                isWin ? 'border-success border-2' : 'border-secondary border-opacity-25'
                                                            }`}
                                                        >
                                                            {/* 1. Cabecera Categoría + Estado */}
                                                            <div className="d-flex justify-content-between align-items-start gap-2 pb-2 mb-2 border-bottom">
                                                                <div>
                                                                    <div className="fw-bold text-dark" style={{ fontSize: '0.95rem' }}>
                                                                        {catKey}
                                                                    </div>
                                                                    {matchedTariff ? (
                                                                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                                            {L.yourVehicle} <strong>{matchedTariff.vehicle_type_name}</strong> ({matchedTariff.max_pax} pax)
                                                                        </div>
                                                                    ) : (
                                                                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                                            {item?.car_model || 'Booking.com'}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <div>
                                                                    {!item ? (
                                                                        <Badge bg="secondary">{L.pendingBadge}</Badge>
                                                                    ) : isWin ? (
                                                                        <Badge bg="success" className="px-2 py-1">
                                                                            <CheckCircle size={12} className="me-1" /> {L.winningBadge}
                                                                        </Badge>
                                                                    ) : (
                                                                        <Badge bg="danger" className="px-2 py-1">
                                                                            <XCircle size={12} className="me-1" /> {L.losingBadge}
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* 2. Comparativa Lado a Lado: Competidor #1 vs Tu Tarifa Actual */}
                                                            <Row className="g-2 mb-2">
                                                                <Col xs={6}>
                                                                    <div className="p-2 rounded bg-light border h-100">
                                                                        <div className="text-muted text-uppercase fw-bold" style={{ fontSize: '9.5px' }}>
                                                                            {L.thCompetitor}
                                                                        </div>
                                                                        {item ? (
                                                                            <>
                                                                                <div className={`fw-bold text-truncate mt-0.5 ${isWin ? 'text-success' : 'text-danger'}`} style={{ fontSize: '12px' }}>
                                                                                    {item.provider || 'Booking.com'}
                                                                                </div>
                                                                                <div className="text-dark fw-bold" style={{ fontSize: '12px' }}>
                                                                                    {simResult?.webWinnerInProviderCurr
                                                                                        ? formatMoney(simResult.webWinnerInProviderCurr, localCurr)
                                                                                        : formatMoney(item.price, route.last_currency || 'CLP')}
                                                                                </div>
                                                                                <div className="text-muted" style={{ fontSize: '9.5px' }}>
                                                                                    {L.includesCommission}
                                                                                </div>
                                                                            </>
                                                                        ) : (
                                                                            <div className="text-muted small mt-1">—</div>
                                                                        )}
                                                                    </div>
                                                                </Col>
                                                                <Col xs={6}>
                                                                    <div className="p-2 rounded bg-light border h-100">
                                                                        <div className="text-muted text-uppercase fw-bold" style={{ fontSize: '9.5px' }}>
                                                                            {L.thYourTariff}
                                                                        </div>
                                                                        {matchedTariff ? (
                                                                            <>
                                                                                <div className="fw-bold text-primary mt-0.5" style={{ fontSize: '12px' }}>
                                                                                    {L.costOneWay} {formatMoney(matchedTariff.cost_local, localCurr)}
                                                                                </div>
                                                                                {matchedTariff.reverse_cost_local !== null && matchedTariff.reverse_cost_local !== undefined && (
                                                                                    <div className="text-muted" style={{ fontSize: '10.5px' }}>
                                                                                        {L.costReturn} <strong>{formatMoney(matchedTariff.reverse_cost_local, localCurr)}</strong>
                                                                                    </div>
                                                                                )}
                                                                                <div className="d-flex flex-wrap gap-1 mt-1">
                                                                                    {(matchedTariff.booking_genius_discount || 0) >= 5 ? (
                                                                                        <Badge bg="primary" style={{ backgroundColor: '#003580', fontSize: '9.5px' }}>
                                                                                            {L.geniusActiveBadge}
                                                                                        </Badge>
                                                                                    ) : (
                                                                                        <Badge bg="white" text="dark" className="border" style={{ fontSize: '9.5px' }}>
                                                                                            {L.geniusInactiveBadge}
                                                                                        </Badge>
                                                                                    )}
                                                                                </div>
                                                                            </>
                                                                        ) : (
                                                                            <Badge bg="warning" text="dark" className="mt-1" style={{ fontSize: '10px' }}>
                                                                                {L.noTariffLoaded}
                                                                            </Badge>
                                                                        )}
                                                                    </div>
                                                                </Col>
                                                            </Row>

                                                            {/* 3. Recomendación para Ganar #1 (Con y Sin Genius) */}
                                                            {simResult && simResult.targetCostNoGenius !== null && (
                                                                <div className="d-flex flex-column gap-1.5 mb-2">
                                                                    <div className="p-2 rounded bg-light border d-flex justify-content-between align-items-center">
                                                                        <div>
                                                                            <div className="text-muted" style={{ fontSize: '10px' }}>{L.withoutGeniusLabel}</div>
                                                                            <div className="fw-bold text-dark" style={{ fontSize: '12px' }}>
                                                                                {L.chargeMax} {formatMoney(simResult.targetCostNoGenius, localCurr)}
                                                                            </div>
                                                                        </div>
                                                                        <Button
                                                                            variant="outline-secondary"
                                                                            size="sm"
                                                                            className="py-1 px-2.5 fw-bold"
                                                                            style={{ fontSize: '11px' }}
                                                                            onClick={() => updateRowSim(simKey, {
                                                                                cost_local: simResult.targetCostNoGenius,
                                                                                genius_active: false
                                                                            }, matchedTariff)}
                                                                        >
                                                                            {L.testBtn}
                                                                        </Button>
                                                                    </div>

                                                                    <div
                                                                        className="p-2 rounded border d-flex justify-content-between align-items-center"
                                                                        style={{ backgroundColor: '#eef4ff', borderColor: '#b8d0ff' }}
                                                                    >
                                                                        <div>
                                                                            <div className="fw-bold" style={{ fontSize: '10px', color: '#003580' }}>
                                                                                {L.withGeniusLabel}
                                                                            </div>
                                                                            <div className="fw-bold" style={{ fontSize: '12px', color: '#003580' }}>
                                                                                {L.tariffUpTo} {formatMoney(simResult.targetCostWithGenius, localCurr)}
                                                                            </div>
                                                                            <div className="text-muted" style={{ fontSize: '10px' }}>
                                                                                {L.netReceive} <strong>{formatMoney(simResult.targetNetWithGenius, localCurr)}</strong>
                                                                                {simResult.canRaiseOrKeepWithGenius && (
                                                                                    <span className="text-success fw-bold ms-1">
                                                                                        {L.withoutLoweringPrice}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                        <Button
                                                                            variant="primary"
                                                                            size="sm"
                                                                            className="py-1 px-2.5 fw-bold text-nowrap"
                                                                            style={{ backgroundColor: '#003580', borderColor: '#003580', fontSize: '11px' }}
                                                                            onClick={() => updateRowSim(simKey, {
                                                                                cost_local: simResult.targetCostWithGenius,
                                                                                genius_active: true,
                                                                                genius_discount: 5
                                                                            }, matchedTariff)}
                                                                        >
                                                                            <Zap size={11} className="me-1" />
                                                                            {L.useGeniusBtn}
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* 4. Calculadora Interactiva + Botón Guardar (Táctil) */}
                                                            {matchedTariff && (
                                                                <div className="p-2.5 rounded-3 bg-light border mt-2">
                                                                    <Row className="g-2 align-items-center mb-2">
                                                                        <Col xs={7}>
                                                                            <Form.Label className="small fw-bold text-dark mb-1" style={{ fontSize: '11px' }}>
                                                                                <Calculator size={12} className="me-1 text-primary" />
                                                                                {L.simulateBaseTariff} ({localCurr})
                                                                            </Form.Label>
                                                                            <InputGroup size="sm">
                                                                                <InputGroup.Text className="fw-bold bg-white">{sym}</InputGroup.Text>
                                                                                <Form.Control
                                                                                    type="number"
                                                                                    step="0.5"
                                                                                    value={simRow.cost_local}
                                                                                    onChange={(e) => updateRowSim(simKey, { cost_local: e.target.value }, matchedTariff)}
                                                                                    className="fw-bold text-dark"
                                                                                />
                                                                            </InputGroup>
                                                                        </Col>
                                                                        <Col xs={5}>
                                                                            <Form.Label className="small fw-bold mb-1" style={{ fontSize: '11px', color: '#003580' }}>
                                                                                {L.geniusProgram}
                                                                            </Form.Label>
                                                                            <Form.Check
                                                                                type="switch"
                                                                                id={`genius-switch-mob-${simKey}`}
                                                                                checked={Boolean(simRow.genius_active)}
                                                                                onChange={(e) => updateRowSim(simKey, {
                                                                                    genius_active: e.target.checked,
                                                                                    genius_discount: 5
                                                                                }, matchedTariff)}
                                                                                label={
                                                                                    <span className="fw-bold" style={{ fontSize: '10.5px', color: simRow.genius_active ? '#003580' : '#6c757d' }}>
                                                                                        {simRow.genius_active ? '-10% Web' : '0%'}
                                                                                    </span>
                                                                                }
                                                                            />
                                                                        </Col>
                                                                    </Row>

                                                                    {simResult && (
                                                                        <div className={`p-2 rounded border mb-2 ${simResult.wouldWin ? 'bg-success bg-opacity-10 border-success' : 'bg-danger bg-opacity-10 border-danger'}`}>
                                                                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-1">
                                                                                <span className={`fw-bold ${simResult.wouldWin ? 'text-success' : 'text-danger'}`} style={{ fontSize: '11.5px' }}>
                                                                                    {simResult.webWinnerClp ? (
                                                                                        simResult.wouldWin ? L.simWinMsg : L.simLoseMsg
                                                                                    ) : (
                                                                                        L.simReadyMsg
                                                                                    )}
                                                                                </span>
                                                                                <span className="text-dark fw-bold" style={{ fontSize: '11px' }}>
                                                                                    {L.netReceive} {formatMoney(simResult.providerNetSimulated, localCurr)}
                                                                                </span>
                                                                            </div>
                                                                            {simResult.simGeniusActive && (
                                                                                <div className="mt-1" style={{ fontSize: '10px', color: '#003580' }}>
                                                                                    ✨ <strong>{L.geniusEffectTitle}</strong> {L.geniusEffectDesc1} ({formatMoney(simResult.simCostLocal * 0.05, localCurr)}) {L.geniusEffectDesc2}
                                                                                </div>
                                                                            )}
                                                                            {!simResult.wouldWin && simResult.webWinnerClp && simResult.gapInProviderCurr > 0 && (
                                                                                <div className="text-danger mt-1" style={{ fontSize: '10px' }}>
                                                                                    {L.lowerMorePrefix} <strong>{formatMoney(simResult.gapInProviderCurr, localCurr)}</strong> {L.lowerMoreMid} {!simResult.simGeniusActive ? L.orActivateGenius : ''}{L.toWinRank1}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    )}

                                                                    <div className="d-flex justify-content-between align-items-center gap-2 pt-1">
                                                                        <Form.Check
                                                                            type="checkbox"
                                                                            id={`both-dir-mob-${simKey}`}
                                                                            checked={Boolean(simRow.both_directions)}
                                                                            onChange={(e) => updateRowSim(simKey, { both_directions: e.target.checked }, matchedTariff)}
                                                                            label={
                                                                                <span className="small fw-semibold text-dark" style={{ fontSize: '11px' }}>
                                                                                    <ArrowLeftRight size={11} className="me-1 text-primary" />
                                                                                    {L.applyBothDirs}
                                                                                </span>
                                                                            }
                                                                        />
                                                                        <Button
                                                                            variant={simResult?.hasChanges ? 'success' : 'primary'}
                                                                            size="sm"
                                                                            className="fw-bold d-flex align-items-center justify-content-center gap-1 shadow-sm px-3 py-1.5"
                                                                            onClick={() => handleApplySimulatedTariff(route, catKey, matchedTariff)}
                                                                            disabled={isSavingThis}
                                                                        >
                                                                            {isSavingThis ? (
                                                                                <>
                                                                                    <Spinner size="sm" animation="border" style={{ width: '12px', height: '12px' }} />
                                                                                    <span>{L.savingBtn}</span>
                                                                                </>
                                                                            ) : (
                                                                                <>
                                                                                    <Save size={13} />
                                                                                    <span>
                                                                                        {simRow.both_directions ? L.saveBothBtn : L.saveOneWayBtn}
                                                                                    </span>
                                                                                </>
                                                                            )}
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                            {/* VISTA ESCRITORIO (COMPUTADOR / TABLET): TABLA COMPLETA DE 7 COLUMNAS */}
                                            <div className="d-none d-md-block">
                                                <Table hover responsive className="mb-0 align-middle small">
                                                    <thead className="bg-dark text-white text-uppercase" style={{ fontSize: '11px' }}>
                                                        <tr>
                                                            <th className="ps-3" style={{ minWidth: '170px' }}>{L.thCategory}</th>
                                                            <th className="text-center">{L.thStatus}</th>
                                                            <th style={{ minWidth: '180px' }}>{L.thCompetitor}</th>
                                                            <th style={{ minWidth: '170px' }}>{L.thYourTariff}</th>
                                                            <th style={{ minWidth: '220px' }}>{L.thRecommendation}</th>
                                                            <th style={{ minWidth: '340px' }} className="bg-primary text-white">
                                                                <Calculator size={13} className="me-1" />
                                                                {L.thCalculator}
                                                            </th>
                                                            <th className="text-end pe-3" style={{ minWidth: '185px' }}>{L.thSave}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {mergedCategories.map(({ catKey, item, matchedTariff }) => {
                                                            const simKey = `${route.id}_${catKey}`;
                                                            const simRow = simState[simKey] || {
                                                                cost_local: matchedTariff?.cost_local ?? '',
                                                                bid_discount: matchedTariff?.booking_bid_discount ?? 0,
                                                                genius_active: (matchedTariff?.booking_genius_discount || 0) >= 5,
                                                                genius_discount: (matchedTariff?.booking_genius_discount || 0) >= 5 ? matchedTariff.booking_genius_discount : 5,
                                                                both_directions: true
                                                            };

                                                            const simResult = computeCategorySimulation(item, matchedTariff, simRow);
                                                            const isWin = Boolean(item?.is_winner);
                                                            const isSavingThis = savingSimKey === simKey;
                                                            const localCurr = matchedTariff?.currency_code || route.last_currency || 'USD';
                                                            const sym = CURRENCY_SYMBOLS[localCurr] || '$';

                                                            return (
                                                                <tr key={catKey} className={isWin ? 'table-success bg-opacity-25' : ''}>
                                                                    {/* 1. CATEGORÍA */}
                                                                    <td className="ps-3">
                                                                        <div className="fw-bold text-dark" style={{ fontSize: '13px' }}>
                                                                            {catKey}
                                                                        </div>
                                                                        {matchedTariff ? (
                                                                            <div className="text-muted" style={{ fontSize: '11px' }}>
                                                                                {L.yourVehicle} <strong>{matchedTariff.vehicle_type_name}</strong> ({matchedTariff.max_pax} pax)
                                                                            </div>
                                                                        ) : (
                                                                            <div className="text-muted" style={{ fontSize: '11px' }}>
                                                                                {item?.car_model || 'Booking.com'}
                                                                            </div>
                                                                        )}
                                                                    </td>

                                                                    {/* 2. ESTADO ACTUAL */}
                                                                    <td className="text-center">
                                                                        {!item ? (
                                                                            <Badge bg="secondary">{L.pendingBadge}</Badge>
                                                                        ) : isWin ? (
                                                                            <Badge bg="success" className="px-2 py-1">
                                                                                <CheckCircle size={12} className="me-1" /> {L.winningBadge}
                                                                            </Badge>
                                                                        ) : (
                                                                            <Badge bg="danger" className="px-2 py-1">
                                                                                <XCircle size={12} className="me-1" /> {L.losingBadge}
                                                                            </Badge>
                                                                        )}
                                                                    </td>

                                                                    {/* 3. COMPETIDOR #1 EN BOOKING */}
                                                                    <td>
                                                                        {item ? (
                                                                            <>
                                                                                <div className={`fw-bold ${isWin ? 'text-success' : 'text-danger'}`}>
                                                                                    {item.provider || 'Booking.com'}
                                                                                </div>
                                                                                <div className="text-dark fw-bold">
                                                                                    {L.publicWebPrice} {simResult?.webWinnerInProviderCurr
                                                                                        ? formatMoney(simResult.webWinnerInProviderCurr, localCurr)
                                                                                        : formatMoney(item.price, route.last_currency || 'CLP')}
                                                                                </div>
                                                                                <div className="text-muted" style={{ fontSize: '10.5px' }}>
                                                                                    {L.includesCommission}
                                                                                </div>
                                                                            </>
                                                                        ) : (
                                                                            <span className="text-muted">—</span>
                                                                        )}
                                                                    </td>

                                                                    {/* 4. TU TARIFA ACTUAL */}
                                                                    <td>
                                                                        {matchedTariff ? (
                                                                            <>
                                                                                <div className="fw-bold text-primary" style={{ fontSize: '13px' }}>
                                                                                    {L.costOneWay} {formatMoney(matchedTariff.cost_local, localCurr)}
                                                                                </div>
                                                                                {matchedTariff.reverse_cost_local !== null && matchedTariff.reverse_cost_local !== undefined && (
                                                                                    <div className="text-muted" style={{ fontSize: '11px' }}>
                                                                                        {L.costReturn} <strong>{formatMoney(matchedTariff.reverse_cost_local, localCurr)}</strong>
                                                                                    </div>
                                                                                )}
                                                                                <div className="d-flex flex-wrap gap-1 mt-1">
                                                                                    {(matchedTariff.booking_genius_discount || 0) >= 5 ? (
                                                                                        <Badge bg="primary" style={{ backgroundColor: '#003580', fontSize: '10px' }}>
                                                                                            {L.geniusActiveBadge} (-{matchedTariff.booking_genius_discount}% {L.youLabel} / -10% Booking)
                                                                                        </Badge>
                                                                                    ) : (
                                                                                        <Badge bg="light" text="dark" className="border" style={{ fontSize: '10px' }}>
                                                                                            {L.geniusInactiveBadge}
                                                                                        </Badge>
                                                                                    )}
                                                                                    {(matchedTariff.booking_bid_discount || 0) > 0 && (
                                                                                        <Badge bg="warning" text="dark" style={{ fontSize: '10px' }}>
                                                                                            {L.bidBadge} -{matchedTariff.booking_bid_discount}%
                                                                                        </Badge>
                                                                                    )}
                                                                                </div>
                                                                            </>
                                                                        ) : (
                                                                            <Badge bg="warning" text="dark">
                                                                                {L.noTariffLoaded}
                                                                            </Badge>
                                                                        )}
                                                                    </td>

                                                                    {/* 5. RECOMENDACIÓN INTELIGENTE PARA GANAR #1 (CON Y SIN GENIUS) */}
                                                                    <td>
                                                                        {simResult && simResult.targetCostNoGenius !== null ? (
                                                                            <div className="d-flex flex-column gap-1">
                                                                                <div className="p-1.5 rounded bg-light border d-flex justify-content-between align-items-center">
                                                                                    <div>
                                                                                        <div className="text-muted" style={{ fontSize: '10px' }}>{L.withoutGeniusLabel}</div>
                                                                                        <div className="fw-bold text-dark">
                                                                                            {L.chargeMax} {formatMoney(simResult.targetCostNoGenius, localCurr)}
                                                                                        </div>
                                                                                    </div>
                                                                                    <Button
                                                                                        variant="outline-secondary"
                                                                                        size="sm"
                                                                                        className="py-0 px-2 fw-bold"
                                                                                        style={{ fontSize: '10.5px' }}
                                                                                        onClick={() => updateRowSim(simKey, {
                                                                                            cost_local: simResult.targetCostNoGenius,
                                                                                            genius_active: false
                                                                                        }, matchedTariff)}
                                                                                    >
                                                                                        {L.testBtn}
                                                                                    </Button>
                                                                                </div>

                                                                                <div
                                                                                    className="p-1.5 rounded border d-flex justify-content-between align-items-center"
                                                                                    style={{ backgroundColor: '#eef4ff', borderColor: '#b8d0ff' }}
                                                                                >
                                                                                    <div>
                                                                                        <div className="fw-bold" style={{ fontSize: '10px', color: '#003580' }}>
                                                                                            {L.withGeniusLabel}
                                                                                        </div>
                                                                                        <div className="fw-bold" style={{ color: '#003580' }}>
                                                                                            {L.tariffUpTo} {formatMoney(simResult.targetCostWithGenius, localCurr)}
                                                                                        </div>
                                                                                        <div className="text-muted" style={{ fontSize: '10px' }}>
                                                                                            {L.netReceive} <strong>{formatMoney(simResult.targetNetWithGenius, localCurr)}</strong>
                                                                                            {simResult.canRaiseOrKeepWithGenius && (
                                                                                                <span className="text-success fw-bold ms-1">
                                                                                                    {L.withoutLoweringPrice}
                                                                                                </span>
                                                                                            )}
                                                                                        </div>
                                                                                    </div>
                                                                                    <Button
                                                                                        variant="primary"
                                                                                        size="sm"
                                                                                        className="py-0 px-2 fw-bold"
                                                                                        style={{ backgroundColor: '#003580', borderColor: '#003580', fontSize: '10.5px' }}
                                                                                        onClick={() => updateRowSim(simKey, {
                                                                                            cost_local: simResult.targetCostWithGenius,
                                                                                            genius_active: true,
                                                                                            genius_discount: 5
                                                                                        }, matchedTariff)}
                                                                                    >
                                                                                        <Zap size={11} className="me-1" />
                                                                                        {L.useGeniusBtn}
                                                                                    </Button>
                                                                                </div>
                                                                            </div>
                                                                        ) : (
                                                                            <span className="text-muted small">
                                                                                {matchedTariff ? L.waitingNextCheck : '—'}
                                                                            </span>
                                                                        )}
                                                                    </td>

                                                                    {/* 6. CALCULADORA / SIMULADOR EN VIVO + SWITCH GENIUS */}
                                                                    <td className="bg-light">
                                                                        {matchedTariff ? (
                                                                            <div className="p-1">
                                                                                <Row className="g-2 align-items-center mb-2">
                                                                                    <Col xs={7}>
                                                                                        <Form.Label className="small fw-bold text-dark mb-1" style={{ fontSize: '11px' }}>
                                                                                            {L.simulateBaseTariff} ({localCurr})
                                                                                        </Form.Label>
                                                                                        <InputGroup size="sm">
                                                                                            <InputGroup.Text className="fw-bold bg-white">{sym}</InputGroup.Text>
                                                                                            <Form.Control
                                                                                                type="number"
                                                                                                step="0.5"
                                                                                                value={simRow.cost_local}
                                                                                                onChange={(e) => updateRowSim(simKey, { cost_local: e.target.value }, matchedTariff)}
                                                                                                className="fw-bold text-dark"
                                                                                            />
                                                                                        </InputGroup>
                                                                                    </Col>
                                                                                    <Col xs={5}>
                                                                                        <Form.Label className="small fw-bold mb-1" style={{ fontSize: '11px', color: '#003580' }}>
                                                                                            {L.geniusProgram}
                                                                                        </Form.Label>
                                                                                        <Form.Check
                                                                                            type="switch"
                                                                                            id={`genius-switch-${simKey}`}
                                                                                            checked={Boolean(simRow.genius_active)}
                                                                                            onChange={(e) => updateRowSim(simKey, {
                                                                                                genius_active: e.target.checked,
                                                                                                genius_discount: 5
                                                                                            }, matchedTariff)}
                                                                                            label={
                                                                                                <span className="fw-bold" style={{ fontSize: '11px', color: simRow.genius_active ? '#003580' : '#6c757d' }}>
                                                                                                    {simRow.genius_active ? L.geniusSwitchActive : L.geniusSwitchInactive}
                                                                                                </span>
                                                                                            }
                                                                                        />
                                                                                    </Col>
                                                                                </Row>

                                                                                {/* Resultado instantáneo de la simulación */}
                                                                                {simResult && (
                                                                                    <div className={`p-2 rounded border ${simResult.wouldWin ? 'bg-success bg-opacity-10 border-success' : 'bg-danger bg-opacity-10 border-danger'}`}>
                                                                                        <div className="d-flex justify-content-between align-items-center">
                                                                                            <span className={`fw-bold ${simResult.wouldWin ? 'text-success' : 'text-danger'}`} style={{ fontSize: '12px' }}>
                                                                                                {simResult.webWinnerClp ? (
                                                                                                    simResult.wouldWin
                                                                                                        ? L.simWinMsg
                                                                                                        : L.simLoseMsg
                                                                                                ) : (
                                                                                                    L.simReadyMsg
                                                                                                )}
                                                                                            </span>
                                                                                            <span className="text-dark fw-bold" style={{ fontSize: '11px' }}>
                                                                                                {L.netReceive} {formatMoney(simResult.providerNetSimulated, localCurr)}
                                                                                            </span>
                                                                                        </div>
                                                                                        {simResult.simGeniusActive && (
                                                                                            <div className="mt-1" style={{ fontSize: '10.5px', color: '#003580' }}>
                                                                                                ✨ <strong>{L.geniusEffectTitle}</strong> {L.geniusEffectDesc1} ({formatMoney(simResult.simCostLocal * 0.05, localCurr)}) {L.geniusEffectDesc2}
                                                                                            </div>
                                                                                        )}
                                                                                        {!simResult.wouldWin && simResult.webWinnerClp && simResult.gapInProviderCurr > 0 && (
                                                                                            <div className="text-danger mt-1" style={{ fontSize: '10.5px' }}>
                                                                                                {L.lowerMorePrefix} <strong>{formatMoney(simResult.gapInProviderCurr, localCurr)}</strong> {L.lowerMoreMid} {!simResult.simGeniusActive ? L.orActivateGenius : ''}{L.toWinRank1}
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        ) : (
                                                                            <span className="text-muted small">{L.notEditable}</span>
                                                                        )}
                                                                    </td>

                                                                    {/* 7. BOTÓN GUARDAR TARIFA (IDA Y VUELTA O SOLO IDA) */}
                                                                    <td className="text-end pe-3">
                                                                        {matchedTariff ? (
                                                                            <div className="d-flex flex-column align-items-end gap-2">
                                                                                <Form.Check
                                                                                    type="checkbox"
                                                                                    id={`both-dir-${simKey}`}
                                                                                    checked={Boolean(simRow.both_directions)}
                                                                                    onChange={(e) => updateRowSim(simKey, { both_directions: e.target.checked }, matchedTariff)}
                                                                                    label={
                                                                                        <span className="small fw-semibold text-dark" style={{ fontSize: '11px' }}>
                                                                                            <ArrowLeftRight size={11} className="me-1 text-primary" />
                                                                                            {L.applyBothDirs}
                                                                                        </span>
                                                                                    }
                                                                                />
                                                                                <Button
                                                                                    variant={simResult?.hasChanges ? 'success' : 'primary'}
                                                                                    size="sm"
                                                                                    className="w-100 fw-bold d-flex align-items-center justify-content-center gap-1 shadow-sm"
                                                                                    onClick={() => handleApplySimulatedTariff(route, catKey, matchedTariff)}
                                                                                    disabled={isSavingThis}
                                                                                >
                                                                                    {isSavingThis ? (
                                                                                        <>
                                                                                            <Spinner size="sm" animation="border" style={{ width: '12px', height: '12px' }} />
                                                                                            <span>{L.savingBtn}</span>
                                                                                        </>
                                                                                    ) : (
                                                                                        <>
                                                                                            <Save size={13} />
                                                                                            <span>
                                                                                                {simRow.both_directions ? L.saveBothBtn : L.saveOneWayBtn}
                                                                                            </span>
                                                                                        </>
                                                                                    )}
                                                                                </Button>
                                                                            </div>
                                                                        ) : (
                                                                            <span className="text-muted small">—</span>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        </div>
                                    )}
                                </Card.Body>
                            )}
                        </Card>
                    );
                })}
                </div>
            )}

            {/* BITÁCORA HISTÓRICA DE CHEQUEOS */}
            <Card className="shadow-sm border-0">
                <Card.Header className="bg-white py-3">
                    <h6 className="fw-bold mb-0"><span>{L.historyTitle}</span></h6>
                    <small className="text-muted"><span>{L.historySub}</span></small>
                </Card.Header>
                <Card.Body className="p-0">
                    {loading ? (
                        <div key="sentinel-logs-loading" className="text-center py-4">
                            <Spinner animation="border" variant="primary" size="sm" className="me-2" />
                            <span>{L.loadingSentinel}</span>
                        </div>
                    ) : logsData.logs.length === 0 ? (
                        <div key="sentinel-logs-empty" className="text-center py-4 text-muted small">
                            <span>{L.emptyHistory}</span>
                        </div>
                    ) : (
                        <div key="sentinel-logs-list">
                            {/* VISTA MÓVIL HISTORIAL */}
                            <div className="d-md-none p-2 bg-light d-flex flex-column gap-2">
                                {logsData.logs.slice(0, 15).map(log => (
                                    <div key={`mob_log_${log.id}`} className="bg-white rounded border p-2.5 shadow-sm">
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                            <span className="fw-bold text-dark small">{log.route_name}</span>
                                            {renderStatusBadge(log.status, log.is_emigo_winner)}
                                        </div>
                                        <div className="d-flex justify-content-between align-items-center text-muted" style={{ fontSize: '11px' }}>
                                            <span>🕒 {formatDateTime(log.timestamp)}</span>
                                            <span className={`fw-bold ${log.is_emigo_winner ? 'text-success' : 'text-danger'}`}>
                                                #1: {log.winning_provider || '—'} ({formatMoney(log.public_price, log.currency)})
                                            </span>
                                        </div>
                                        {log.categories_breakdown && log.categories_breakdown.length > 0 && (
                                            <div className="mt-2 text-end">
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    className="fw-bold py-0.5 px-2 w-100"
                                                    style={{ fontSize: '11px' }}
                                                    onClick={() => setSelectedLogDetails(log)}
                                                >
                                                    <span>{L.viewCategoriesBtn} ({log.categories_breakdown.length})</span>
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* VISTA ESCRITORIO HISTORIAL */}
                            <div className="d-none d-md-block">
                                <Table hover responsive className="mb-0 align-middle small">
                                    <thead className="bg-light text-uppercase text-muted">
                                        <tr>
                                            <th className="ps-3">{L.thDateTime}</th>
                                            <th>{L.thRoute}</th>
                                            <th>{L.thSearchedDate}</th>
                                            <th className="text-center">{L.thResultStd}</th>
                                            <th>{L.thProvider1}</th>
                                            <th className="text-end">{L.thPublicWinner}</th>
                                            <th className="text-center pe-3">{L.thDetail}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {logsData.logs.slice(0, 25).map(log => (
                                            <tr key={log.id}>
                                                <td className="ps-3 text-nowrap">{formatDateTime(log.timestamp)}</td>
                                                <td>
                                                    <div className="fw-bold">{log.route_name}</div>
                                                    <div className="text-muted" style={{ fontSize: '11px' }}>{COUNTRY_FLAGS[log.country] || log.country}</div>
                                                </td>
                                                <td className="text-muted">{log.pickup_datetime_searched || '—'}</td>
                                                <td className="text-center">
                                                    {renderStatusBadge(log.status, log.is_emigo_winner)}
                                                </td>
                                                <td className={`fw-bold ${log.is_emigo_winner ? 'text-success' : 'text-danger'}`}>
                                                    {log.winning_provider || '—'}
                                                </td>
                                                <td className="text-end fw-bold">
                                                    {formatMoney(log.public_price, log.currency)}
                                                </td>
                                                <td className="text-center pe-3">
                                                    {log.categories_breakdown && log.categories_breakdown.length > 0 ? (
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            className="fw-bold py-0 px-2"
                                                            onClick={() => setSelectedLogDetails(log)}
                                                        >
                                                            <span>{L.viewCategoriesBtn} ({log.categories_breakdown.length})</span>
                                                        </Button>
                                                    ) : (
                                                        <span className="text-muted">{log.notes || '—'}</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* MODAL CREAR / EDITAR RUTA */}
            <Modal show={showRouteModal} onHide={() => setShowRouteModal(false)} centered size="lg">
                <Form onSubmit={handleSaveRoute}>
                    <Modal.Header closeButton>
                        <Modal.Title className="fw-bold">
                            {editingRoute ? `${L.modalEditTitle} ${editingRoute.name}` : L.modalCreateTitle}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Row className="g-3">
                            {providerZonePairs.length > 0 && (
                                <Col md={12}>
                                    <Form.Label className="fw-bold small text-primary">
                                        {L.step1SelectZones}
                                    </Form.Label>
                                    <Form.Select
                                        value={formData.zone_pair_key}
                                        onChange={(e) => handleZonePairSelect(e.target.value)}
                                        required={isProviderMode}
                                    >
                                        <option value="">{L.selectZonePlaceholder}</option>
                                        {providerZonePairs.map(pair => (
                                            <option key={pair.key} value={pair.key}>
                                                {pair.label} ({COUNTRY_FLAGS[pair.country] || pair.country})
                                            </option>
                                        ))}
                                    </Form.Select>
                                    <Form.Text className="text-muted">
                                        {L.step1Help}
                                    </Form.Text>
                                </Col>
                            )}

                            <Col md={8}>
                                <Form.Label className="fw-bold small">{L.routeShortName}</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Ex: GRU ➔ Hotel Transamerica Berrini"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    required
                                />
                            </Col>
                            <Col md={4}>
                                <Form.Label className="fw-bold small">{L.countryField}</Form.Label>
                                <Form.Select
                                    value={formData.country}
                                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                                    disabled={isProviderMode && Boolean(formData.zone_pair_key)}
                                >
                                    <option value="Chile">🇨🇱 Chile</option>
                                    <option value="Brasil">🇧🇷 Brasil</option>
                                    <option value="Peru">🇵🇪 Perú</option>
                                    <option value="Argentina">🇦🇷 Argentina</option>
                                    <option value="Dominican Republic">🇩🇴 Rep. Dominicana</option>
                                </Form.Select>
                            </Col>

                            <Col md={12}>
                                <Form.Label className="fw-bold small">{L.originField}</Form.Label>
                                <AddressAutocomplete
                                    value={formData.origin_text}
                                    onChange={(val) => setFormData(prev => ({ ...prev, origin_text: val }))}
                                    onSelect={(p) => setFormData(prev => ({ ...prev, origin_text: p.address }))}
                                    countryCode={COUNTRY_TO_ISO_CODE[formData.country]}
                                    placeholder="Ex: Guarulhos International Airport (GRU), Sao Paulo, Brazil"
                                    required
                                />
                                <Form.Text className="text-muted" style={{ fontSize: '11px' }}>
                                    📍 {language === 'pt'
                                        ? 'Digite e selecione um endereço, hotel ou aeroporto válido da lista sugerida.'
                                        : language === 'en'
                                            ? 'Type and select a valid address, hotel, or airport from the suggested list.'
                                            : 'Escribe y selecciona una dirección, hotel o aeropuerto válido de la lista sugerida.'}
                                </Form.Text>
                            </Col>

                            <Col md={12}>
                                <Form.Label className="fw-bold small">{L.destField}</Form.Label>
                                <AddressAutocomplete
                                    value={formData.destination_text}
                                    onChange={(val) => setFormData(prev => ({ ...prev, destination_text: val }))}
                                    onSelect={(p) => setFormData(prev => ({ ...prev, destination_text: p.address }))}
                                    countryCode={COUNTRY_TO_ISO_CODE[formData.country]}
                                    placeholder="Ex: Hotel Transamerica Berrini, Sao Paulo, Brazil"
                                    required
                                />
                                <Form.Text className="text-muted" style={{ fontSize: '11px' }}>
                                    📍 {language === 'pt'
                                        ? 'Digite o nome do hotel, rua ou ponto de referência dentro da zona e escolha nas sugestões.'
                                        : language === 'en'
                                            ? 'Type the hotel, street, or landmark inside the zone and pick from suggestions.'
                                            : 'Escribe el nombre del hotel, calle o punto dentro de la zona y elígelo de las sugerencias.'}
                                </Form.Text>
                            </Col>

                            <Col md={4}>
                                <Form.Label className="fw-bold small">{L.paxField}</Form.Label>
                                <Form.Control
                                    type="number"
                                    min={1}
                                    max={15}
                                    value={formData.passengers}
                                    onChange={(e) => setFormData({ ...formData, passengers: parseInt(e.target.value) || 2 })}
                                />
                            </Col>
                            <Col md={4}>
                                <Form.Label className="fw-bold small">{L.daysAheadField}</Form.Label>
                                <Form.Control
                                    type="number"
                                    min={1}
                                    max={30}
                                    value={formData.days_ahead}
                                    onChange={(e) => setFormData({ ...formData, days_ahead: parseInt(e.target.value) || 3 })}
                                />
                            </Col>
                            <Col md={4}>
                                <Form.Label className="fw-bold small">{L.pickupTimeField}</Form.Label>
                                <Form.Control
                                    type="time"
                                    value={formData.pickup_time}
                                    onChange={(e) => setFormData({ ...formData, pickup_time: e.target.value })}
                                />
                            </Col>

                            <Col md={12}>
                                <Form.Check
                                    type="switch"
                                    id="sentinel-route-active"
                                    label={L.activeSwitchLabel}
                                    checked={formData.is_active}
                                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                />
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowRouteModal(false)}>
                            {L.cancelBtn}
                        </Button>
                        <Button variant="primary" type="submit" disabled={savingRoute} className="fw-bold">
                            {savingRoute ? <Spinner size="sm" animation="border" /> : L.saveRouteBtn}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* MODAL DETALLE DE UN LOG HISTÓRICO */}
            <Modal show={!!selectedLogDetails} onHide={() => setSelectedLogDetails(null)} centered size="lg">
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="fw-bold fs-6">
                        {L.modalDetailTitle} — {selectedLogDetails?.route_name} ({selectedLogDetails?.pickup_datetime_searched})
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedLogDetails && (
                        <Table bordered hover responsive size="sm" className="align-middle small mb-0">
                            <thead className="bg-dark text-white">
                                <tr>
                                    <th>{L.thCategory}</th>
                                    <th className="text-center">{L.thStatus}</th>
                                    <th>{L.thProvider1}</th>
                                    <th className="text-end">{L.thPublicWinner}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(selectedLogDetails.categories_breakdown || []).map((item, idx) => (
                                    <tr key={idx} className={item.is_winner ? 'table-success' : ''}>
                                        <td className="fw-bold">{item.category || `#${idx + 1}`}</td>
                                        <td className="text-center">
                                            {item.is_winner ? (
                                                <Badge bg="success">{L.winningBadge}</Badge>
                                            ) : (
                                                <Badge bg="danger">{L.losingBadge}</Badge>
                                            )}
                                        </td>
                                        <td>{item.provider || '—'}</td>
                                        <td className="text-end fw-bold">{formatMoney(item.price, selectedLogDetails.currency || 'CLP')}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default BookingSentinelView;
